import PhoneInventoryPanel from '../components/mulehunter/PhoneInventoryPanel';
import { isFeatureAllowed } from '../auth/features';
import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as React from 'react';
import {
    Radio, MessageSquare, Users, Eye, RefreshCw, Plus, Play, Trash2, Lock,
    Shield, AlertTriangle, Globe, Network, ShoppingBag, Hash, Link2, X, Copy,
    ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, Building2, Twitter, ExternalLink, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import api, { authService } from '../services/api';
import TelegramConnections from '../components/telegram/TelegramConnections';
import TelegramCollectionStatus from '../components/telegram/TelegramCollectionStatus';
import ChannelValidationModal, { type ChannelValidationResult } from '../components/ChannelValidationModal';
import {
    brandPrimaryBtnMd, brandChipBrand, brandChipMuted, brandIconAccent,
} from '../components/brandChrome';
import {
    CategoryBadge,
    FIU_STATUS_LABELS,
    FIU_STATUS_TO_SEVERITY,
    IntelStatIcon,
    SeverityBadge,
} from '../components/intel';
import {
    formatScrapeAttemptStatus,
    zeroMessageDiagnostic,
    type ScrapeAttempt,
} from './telegramIntelHelpers';

export type { ScrapeAttempt } from './telegramIntelHelpers';
export {
    formatRelativeAgo,
    formatScrapeAttemptStatus,
    zeroMessageDiagnostic,
} from './telegramIntelHelpers';

// ─── Types ─────────────────────────────────────────────────────────

type Visibility = 'public' | 'private';

interface Channel {
    name: string;
    enabled: boolean;
    visibility: Visibility;
    owner_user_id: number | null;
    owner_username?: string | null;
    source?: string | null;
    last_scraped?: string | null;
    last_scrape_attempt?: ScrapeAttempt | null;
    health_status?: 'healthy' | 'quarantined' | 'rate_limited' | 'unreachable' | string;
    quarantine_reason?: string | null;
    quarantined_at?: string | null;
    last_error_type?: string | null;
    last_error_msg?: string | null;
    failure_count?: number;
    tier?: number;
}

interface TelegramMessage {
    id: number; channel: string; message_id?: number; content: string;
    upi_ids: string[]; crypto_addresses: string[];
    extracted_exchanges?: string[]; sender: string;
    scraped_at: string | null;
    is_relevant?: boolean;
    topic_tags?: string[];
    // Enriched from raw_archive (via LEFT JOIN in the backend) — same
    // signals the News tab already surfaces, now available on Messages.
    has_media?: boolean;
    media_type?: string | null;
    extracted_urls?: string[];
    extracted_domains?: string[];
    tg_group_refs?: string[];
    tx_hashes?: string[];
}

interface Sender {
    sender_id: number | null; sender_name: string; sender_username: string | null;
    message_count: number; channel_count: number;
    unique_upi_count: number; unique_wallet_count: number;
    avg_messages_per_day: number; risk_score: number;
    risk_flags: string[]; first_seen: string | null; last_seen: string | null;
    actor_type?: string;
    geo_scope?: string;
    notes?: string;
    bio?: string;
    discovered_links?: string[];
}

interface CrossChannelUser {
    sender_id: number; sender_name: string; sender_username: string;
    channels: string[]; channel_count: number; total_messages: number;
    has_crypto_addresses: boolean; has_trade_intent: boolean;
    crypto_addresses_seen: string[]; services_mentioned: string[];
}

interface ScamDomain {
    domain: string; mention_count: number; channels_seen: string[];
    classification: string; is_confirmed: boolean;
}

interface NetworkRef { target_group: string; total_refs: number; from_channels: string[] }
interface UrlTxHash { tx_hash: string; chain_guess: string; explorer_domain: string; channel_name: string; message_date: string }
interface TopicItem { tag: string; cnt: number }
interface DomainCount { domain: string; cnt: number }
interface OnionDomain { domain: string; cnt: number }
interface MarketplaceListing { marketplace_tag: string; channel_name: string; sender_username: string; message_date: string; preview: string }

interface ArchiveStats {
    total_messages: number; processed: number; spam_messages: number;
    marketplace_listings: number; onion_messages: number;
    with_crypto_addrs: number; with_tx_hashes: number;
    channels: number; unique_senders: number;
    oldest_message: string; newest_message: string;
}

// Whole-lake row counts, straight from the backend. Separate from ArchiveStats,
// which only covers telegram_raw_archive.
interface LakeTotals {
    channels: number;
    p2p_messages: number;
    raw_archive: number;
    scam_domains: number;
    cross_source_hits: number;
    cross_channel_users: number;
}

interface DeepIntelData {
    lake_totals?: LakeTotals;
    archive: ArchiveStats;
    topic_distribution: TopicItem[];
    scam_domains: ScamDomain[];
    cross_channel_users: { total: number; top: CrossChannelUser[] };
    network_graph: { unique_groups: number; top_refs: NetworkRef[] };
    url_tx_hashes: { total: number; samples: UrlTxHash[] };
    top_domains: DomainCount[];
    marketplace_listings: MarketplaceListing[];
    onion_domains: OnionDomain[];
}

interface CrossHit {
    hit_type?: string;
    wallet_address?: string | null;
    upi_id?: string | null;
    entity_name?: string | null;
    source?: string | null;
    sender_username?: string | null;
    sender_name?: string | null;
    channel_name?: string | null;
    confidence?: number | null;
    first_seen?: string | null;
    notes?: string | null;
}

interface IntelSummary {
    summary: { entity_links: number; sender_features: number; cross_source_hits: number; artifact_messages: number; phones_found: number; emails_found: number };
    top_senders: Sender[];
    // The backend returns top_cross_hits via /p2p/telegram/intel-summary
    // (Telegram identity matched to P2P merchant, sanctions hit, or entity DB
    // attribution). It was being dropped at the type boundary until Jun 2026.
    top_cross_hits?: CrossHit[];
}

// ─── Helpers ───────────────────────────────────────────────────────

const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : '-';
const fmtNum = (n: number) => n.toLocaleString();
const truncHash = (h: string) => h.length > 20 ? `${h.slice(0, 12)}...${h.slice(-6)}` : h;

const TOPIC_COLORS: Record<string, string> = {
    trade_intent: 'bg-green-500/15 text-green-700 dark:text-green-400',
    scam_report: 'bg-red-500/15 text-red-700 dark:text-red-400',
    victim_report: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
    spam: 'bg-red-500/15 text-red-700 dark:text-red-300',
    scam_domain: 'bg-red-500/15 text-red-700 dark:text-red-500',
    darknet: brandChipMuted,
    dex_discussion: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
    mixer_discussion: 'bg-amber-500/15 text-amber-800 dark:text-amber-400',
    regulatory: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400',
    privacy_tech: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
    mining: 'bg-amber-500/15 text-amber-800 dark:text-amber-400',
    price_discussion: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    marketplace: 'bg-muted text-foreground',
};

type TabId = 'channels' | 'messages' | 'news' | 'entities' | 'senders' | 'deep' | 'exchanges' | 'osint' | 'connections' | 'phones';

type TelegramTabDef = { id: TabId; label: string; icon: React.ReactNode; badge?: string };

function TelegramTabBar({
    tabs,
    activeTab,
    onSelect,
}: {
    tabs: TelegramTabDef[];
    activeTab: TabId;
    onSelect: (id: TabId) => void;
}) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [overflow, setOverflow] = useState({ left: false, right: false });

    const updateOverflow = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const left = el.scrollLeft > 2;
        const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
        setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        updateOverflow();
        const onScroll = () => updateOverflow();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', updateOverflow);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOverflow) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', updateOverflow);
            ro?.disconnect();
        };
    }, [updateOverflow]);

    const tabSignature = tabs.map((t) => `${t.id}:${t.badge ?? ''}`).join('|');

    useEffect(() => {
        updateOverflow();
        const el = scrollerRef.current;
        const btn = el?.querySelector<HTMLElement>(`[data-tab="${activeTab}"]`);
        if (!el || !btn || el.clientWidth === 0) return;
        const left = btn.offsetLeft;
        const right = left + btn.offsetWidth;
        const viewLeft = el.scrollLeft;
        const viewRight = viewLeft + el.clientWidth;
        const pad = 40;
        if (left < viewLeft + pad) {
            const next = Math.max(0, left - pad);
            if (typeof el.scrollTo === 'function') el.scrollTo({ left: next, behavior: 'smooth' });
            else el.scrollLeft = next;
        } else if (right > viewRight - pad) {
            const next = right - el.clientWidth + pad;
            if (typeof el.scrollTo === 'function') el.scrollTo({ left: next, behavior: 'smooth' });
            else el.scrollLeft = next;
        }
    }, [activeTab, tabSignature, updateOverflow]);

    const scrollByDir = (dir: -1 | 1) => {
        const el = scrollerRef.current;
        if (!el) return;
        const delta = dir * 220;
        if (typeof el.scrollBy === 'function') el.scrollBy({ left: delta, behavior: 'smooth' });
        else el.scrollLeft += delta;
    };

    return (
        <div className="relative mb-6 min-w-0">
            {overflow.left && (
                <div className="absolute left-0 top-0 bottom-1 z-10 flex items-center bg-gradient-to-r from-background via-background/90 to-transparent pr-8 pointer-events-none">
                    <button
                        type="button"
                        aria-label="Scroll tabs left"
                        onClick={() => scrollByDir(-1)}
                        className="pointer-events-auto p-1 rounded-md bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted shadow-sm"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                </div>
            )}
            <div
                ref={scrollerRef}
                role="tablist"
                aria-label="Telegram Intelligence sections"
                data-testid="telegram-tab-bar"
                className="flex gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain border-b border-border pb-1 scroll-smooth styled-scrollbar"
            >
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        data-tab={t.id}
                        aria-selected={activeTab === t.id}
                        onClick={() => onSelect(t.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors shrink-0 whitespace-nowrap ${
                            activeTab === t.id
                                ? 'bg-card border border-border border-b-transparent text-foreground -mb-px'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                    >
                        {t.icon}
                        {t.label}
                        {t.badge && <span className="text-xs px-1.5 py-0.5 bg-muted rounded">{t.badge}</span>}
                    </button>
                ))}
                <span className="shrink-0 w-10" aria-hidden />
            </div>
            {overflow.right && (
                <div className="absolute right-0 top-0 bottom-1 z-10 flex items-center justify-end bg-gradient-to-l from-background via-background/90 to-transparent pl-8 pointer-events-none">
                    <button
                        type="button"
                        aria-label="Scroll tabs right"
                        onClick={() => scrollByDir(1)}
                        className="pointer-events-auto p-1 rounded-md bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted shadow-sm"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
}

type EntityType = 'urls' | 'domains' | 'onion_domains' | 'tg_group_refs' | 'upi_ids' | 'crypto_addresses' | 'tx_hashes' | 'mentioned_services';

interface AggregatedEntity {
    value: string;
    hits: number;
    channel_count: number;
    channels: string[];
    first_seen: string | null;
    last_seen: string | null;
    sample_message_ids: number[];
}
type MsgScope = 'all' | 'public' | 'fiu-ind' | 'private';
type NewsCategory = 'news' | 'p2p' | 'all';

interface NewsMessage {
    channel: string;
    message_id: number;
    message_date: string | null;
    content: string;
    sender: string;
    sender_username: string | null;
    has_media: boolean;
    media_type: string | null;
    forward_from: string | null;
    views: number | null;
    upi_ids: string[];
    crypto_addresses: string[];
    tx_hashes: string[];
    mentioned_services: string[];
    mentioned_amounts: string[];
    topic_tags: string[];
    extracted_domains: string[];
    onion_domains: string[];
    is_relevant: boolean;
    relevance_reason: string | null;
    scraped_at: string | null;
    geography?: { states: string[]; countries: string[]; evidence: { value: string; quote: string }[] };
}

type ProviderCategory = 'fiu_onshore' | 'fiu_offshore' | 'offshore' | 'local_p2p';
type FiuStatus = 'registered' | 'show_cause' | 'pending' | 'unregistered' | 'banned';

interface Provider {
    id: number;
    slug: string;
    display_name: string;
    category: ProviderCategory;
    fiu_status: FiuStatus;
    fiu_registration_date: string | null;
    website: string | null;
    twitter_handle: string | null;
    telegram_public_handle: string | null;
    telegram_invite_url: string | null;
    notes: string | null;
    telegram_signals: { msg_30d: number; msg_total: number; last_announcement_at: string | null } | null;
    p2p_signals: { p2p_ads_7d: number; spot_rows_7d: number; last_p2p_scrape: string | null } | null;
}

// ─── Main Component ────────────────────────────────────────────────

export default function TelegramIntelligence() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<TabId>('messages');
    const [loading, setLoading] = useState(false);

    // Auth context (used for ownership-aware grouping)
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [canAddPublic, setCanAddPublic] = useState(false);

    // Channels tab state
    const [channels, setChannels] = useState<Channel[]>([]);
    const [newChannel, setNewChannel] = useState('');
    const [newChannelVisibility, setNewChannelVisibility] = useState<Visibility>('private');
    const [validationOpen, setValidationOpen] = useState(false);
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState<ChannelValidationResult | null>(null);
    const [scrapeRunning, setScrapeRunning] = useState(false);
    const [scrapeJobId, setScrapeJobId] = useState<string | null>(null);
    const [scrapeStatus, setScrapeStatus] = useState<string>('');
    const [scrapeLimit, setScrapeLimit] = useState(100);
    const scrapePollRef = useRef<number | null>(null);
    const [deepBackfillRunning, setDeepBackfillRunning] = useState(false);

    // Messages tab state — seed from ?q= so deep-links open filtered
    const urlQ = (searchParams.get('q') || '').trim();
    const [messages, setMessages] = useState<TelegramMessage[]>([]);
    const [msgTotal, setMsgTotal] = useState(0);
    const [msgPage, setMsgPage] = useState(1);
    const [msgSearch, setMsgSearch] = useState(urlQ);
    const [msgScope, setMsgScope] = useState<MsgScope>('all');
    const [msgRelevantOnly, setMsgRelevantOnly] = useState(true);
    const [msgChannelScrapeStatus, setMsgChannelScrapeStatus] = useState<ScrapeAttempt | null>(null);
    const [msgFilteredChannel, setMsgFilteredChannel] = useState<string | null>(null);

    // News & Intel tab state (queries telegram_raw_archive)
    const [news, setNews] = useState<NewsMessage[]>([]);
    const [newsTotal, setNewsTotal] = useState(0);
    const [newsPage, setNewsPage] = useState(1);
    const [newsScope, setNewsScope] = useState<MsgScope>('all');
    const [newsCategory, setNewsCategory] = useState<NewsCategory>('all');
    const [newsState, setNewsState] = useState('');
    const [newsCountry, setNewsCountry] = useState('');
    const [newsTopic, setNewsTopic] = useState<string>('');
    const [newsSearch, setNewsSearch] = useState('');

    // Entities tab — aggregates extracted signals (URLs, domains,
    // TG handles, UPIs, wallets, tx hashes) across the caller's visible
    // channels. Drill-down opens a modal listing the source messages
    // that carried that specific value.
    const [entityType, setEntityType] = useState<EntityType>('domains');
    const [entityScope, setEntityScope] = useState<MsgScope>('all');
    const [entityChannel, setEntityChannel] = useState<string>('');
    const [entitySearch, setEntitySearch] = useState('');
    const [entityDays, setEntityDays] = useState(90);
    const [entities, setEntities] = useState<AggregatedEntity[]>([]);
    const [entitiesTotal, setEntitiesTotal] = useState(0);
    const [entityDrill, setEntityDrill] = useState<{ type: EntityType; value: string; messages: TelegramMessage[]; total: number; loading: boolean } | null>(null);

    // Senders tab state
    const [intelSummary, setIntelSummary] = useState<IntelSummary | null>(null);
    const intelSummaryRef = useRef(intelSummary);
    intelSummaryRef.current = intelSummary;
    const intelAbortRef = useRef<AbortController | null>(null);

    // Deep analysis tab state
    const [deepData, setDeepData] = useState<DeepIntelData | null>(null);
    const deepDataRef = useRef(deepData);
    deepDataRef.current = deepData;
    const deepAbortRef = useRef<AbortController | null>(null);
    const [processing, setProcessing] = useState(false);
    const [expandedSection, setExpandedSection] = useState<string | null>('overview');

    // Exchanges tab state — VDA SP directory
    const [providers, setProviders] = useState<Provider[]>([]);
    const [providerCategories, setProviderCategories] = useState<Set<ProviderCategory>>(
        new Set<ProviderCategory>(['fiu_onshore', 'fiu_offshore', 'offshore', 'local_p2p'])
    );
    const [providerSearch, setProviderSearch] = useState('');
    const [expandedProviderId, setExpandedProviderId] = useState<number | null>(null);

    // ── Data fetching ──────────────────────────────────────────────

    const fetchChannels = useCallback(async () => {
        try {
            const res = await api.get('/scrapers/telegram/channels');
            setChannels(res.data.channels || []);
            if (typeof res.data.current_user_id === 'number') setCurrentUserId(res.data.current_user_id);
            if (typeof res.data.can_add_public === 'boolean') setCanAddPublic(res.data.can_add_public);
        } catch (e) { console.error('Failed to fetch channels:', e); }
    }, []);

    const fetchMessages = useCallback(async (
        page = 1,
        scope: MsgScope = msgScope,
        searchText: string = msgSearch,
        relevantOnly: boolean = msgRelevantOnly,
    ) => {
        setLoading(true);
        try {
            const params: Record<string, any> = {
                limit: 30,
                offset: (page - 1) * 30,
                scope,
                relevant_only: relevantOnly,
            };
            const trimmed = (searchText || '').trim();
            if (trimmed) params.q = trimmed;
            const res = await api.get('/scrapers/telegram/messages', { params });
            setMessages(res.data.messages || []);
            setMsgTotal(res.data.total || 0);
            setMsgPage(page);
            setMsgFilteredChannel(res.data.filtered_channel || null);
            setMsgChannelScrapeStatus(res.data.channel_scrape_status || null);
        } catch (e) { console.error('Failed to fetch messages:', e); }
        finally { setLoading(false); }
    }, [msgScope, msgSearch, msgRelevantOnly]);

    const newsAbortRef = useRef<AbortController | null>(null);
    const [newsError, setNewsError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const fetchNews = useCallback(async (
        page = 1,
        scope: MsgScope = newsScope,
        category: NewsCategory = newsCategory,
        topic: string = newsTopic,
        q: string = newsSearch,
    ) => {
        newsAbortRef.current?.abort();
        const ac = new AbortController();
        newsAbortRef.current = ac;
        setLoading(true); setNewsError('');
        try {
            const params: any = {
                limit: 30,
                offset: (page - 1) * 30,
                scope,
                category,
            };
            if (newsState) params.state = newsState;
            if (newsCountry.length === 2) params.country = newsCountry;
            if (topic) params.topic = topic;
            if (q.trim()) params.q = q.trim();
            const res = await api.get('/scrapers/telegram/news', { params, signal: ac.signal });
            if (ac.signal.aborted) return;
            setNews(res.data.messages || []);
            setNewsTotal(res.data.total || 0);
            setNewsPage(page);
        } catch {
            if (!ac.signal.aborted) { setNewsError('News could not be loaded. Results for these filters are unavailable.'); setNews([]); setNewsTotal(0); }
        } finally { if (!ac.signal.aborted) setLoading(false); }
    }, [newsScope, newsCategory, newsTopic, newsSearch, newsState, newsCountry]);

    const fetchIntelSummary = useCallback(async () => {
        intelAbortRef.current?.abort();
        const ac = new AbortController();
        intelAbortRef.current = ac;
        if (!intelSummaryRef.current) setLoading(true);
        try {
            const res = await api.get('/p2p/telegram/intel-summary', {
                signal: ac.signal,
                timeout: 60_000,
            });
            if (!ac.signal.aborted) setIntelSummary(res.data);
        } catch (e: any) {
            if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
            console.error('Failed to fetch intel summary:', e);
        } finally {
            if (!ac.signal.aborted) setLoading(false);
        }
    }, []);

    const fetchEntities = useCallback(async (
        type: EntityType = entityType,
        scope: MsgScope = entityScope,
        channel: string = entityChannel,
        days: number = entityDays,
        search: string = entitySearch,
    ) => {
        setLoading(true);
        try {
            const params: Record<string, any> = { entity_type: type, scope, days, limit: 200 };
            if (channel) params.channel = channel;
            const trimmed = search.trim();
            if (trimmed) params.q = trimmed;
            const res = await api.get('/scrapers/telegram/entities', { params });
            setEntities(res.data.entities || []);
            setEntitiesTotal(res.data.total_distinct || 0);
        } catch (e) { console.error('Failed to fetch entities:', e); setEntities([]); setEntitiesTotal(0); }
        finally { setLoading(false); }
    }, [entityType, entityScope, entityChannel, entityDays, entitySearch]);

    const openEntityDrilldown = useCallback(async (type: EntityType, value: string) => {
        setEntityDrill({ type, value, messages: [], total: 0, loading: true });
        try {
            const params: Record<string, any> = {
                entity_type: type, value,
                scope: entityScope,
                limit: 50,
            };
            if (entityChannel) params.channel = entityChannel;
            const res = await api.get('/scrapers/telegram/messages-by-entity', { params });
            setEntityDrill({ type, value, messages: res.data.messages || [], total: res.data.total || 0, loading: false });
        } catch (e) {
            console.error('Drill-down failed:', e);
            setEntityDrill({ type, value, messages: [], total: 0, loading: false });
        }
    }, [entityScope, entityChannel]);

    const fetchDeepIntel = useCallback(async (fresh = false) => {
        deepAbortRef.current?.abort();
        const ac = new AbortController();
        deepAbortRef.current = ac;
        if (!deepDataRef.current) setLoading(true);
        try {
            const res = await api.get('/scrapers/telegram/intelligence-summary', {
                params: fresh ? { fresh: true } : undefined,
                signal: ac.signal,
                timeout: 60_000,
            });
            if (!ac.signal.aborted) setDeepData(res.data);
        } catch (e: any) {
            if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
            console.error('Failed to fetch deep intel:', e);
        } finally {
            if (!ac.signal.aborted) setLoading(false);
        }
    }, []);

    const fetchProviders = useCallback(async () => {
        try {
            const res = await api.get('/scrapers/telegram/providers');
            setProviders(res.data.providers || []);
        } catch (e) { console.error('Failed to fetch providers:', e); }
    }, []);

    useEffect(() => {
        // Bootstrap currentUserId via authService when channel list is empty (e.g.
        // first render before list endpoint replies). The list endpoint also
        // returns it, so this is just for the very first paint of the visibility
        // toggle.
        authService.getMe().then((me: any) => {
            if (me?.id) setCurrentUserId(prev => prev ?? me.id);
            if (isFeatureAllowed(me?.features, me?.role, 'admin.tools')) setCanAddPublic(true);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        fetchChannels();
        if (activeTab === 'messages') fetchMessages(1, msgScope);
        if (activeTab === 'news') fetchNews(1, newsScope, newsCategory, newsTopic, newsSearch);
        if (activeTab === 'senders') fetchIntelSummary();
        // Not gated on the 'deep' tab: the lake overview below the header shows on
        // every tab, and the backend caches this summary for 5 minutes.
        fetchDeepIntel();
        if (activeTab === 'exchanges') fetchProviders();
        if (activeTab === 'entities') fetchEntities();
        // msgScope deliberately omitted — scope changes refetch via its own handler.
    }, [activeTab]);

    useEffect(() => { if (activeTab === 'news' && (!newsCountry || newsCountry.length === 2)) void fetchNews(1); }, [newsState, newsCountry]);

    useEffect(() => {
        return () => {
            newsAbortRef.current?.abort();
            intelAbortRef.current?.abort();
            deepAbortRef.current?.abort();
        };
    }, []);

    // TG-4: honour ?q=<channel> deep-links — open Messages with active filter chip.
    useEffect(() => {
        const q = (searchParams.get('q') || '').trim();
        if (!q) {
            if (!msgSearch && !msgFilteredChannel && !msgChannelScrapeStatus) return;
            setMsgSearch('');
            setMsgFilteredChannel(null);
            setMsgChannelScrapeStatus(null);
            if (activeTab === 'messages') {
                fetchMessages(1, msgScope, '', msgRelevantOnly);
            }
            return;
        }
        if (q === msgSearch && activeTab === 'messages') return;
        setMsgSearch(q);
        setActiveTab('messages');
        fetchMessages(1, msgScope, q, msgRelevantOnly);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const clearChannelFilter = useCallback(() => {
        setMsgSearch('');
        setMsgFilteredChannel(null);
        setMsgChannelScrapeStatus(null);
        const next = new URLSearchParams(searchParams);
        next.delete('q');
        setSearchParams(next, { replace: true });
        fetchMessages(1, msgScope, '', msgRelevantOnly);
    }, [searchParams, setSearchParams, fetchMessages, msgScope, msgRelevantOnly]);

    const applyChannelFilter = useCallback((handle: string) => {
        const name = handle.replace(/^@/, '').trim();
        if (!name) return;
        setMsgSearch(name);
        setActiveTab('messages');
        const next = new URLSearchParams(searchParams);
        next.set('q', name);
        setSearchParams(next, { replace: true });
        fetchMessages(1, msgScope, name, msgRelevantOnly);
    }, [searchParams, setSearchParams, fetchMessages, msgScope, msgRelevantOnly]);

    // ── Actions ────────────────────────────────────────────────────

    // Step 1: validate the handle (Telethon resolve) and open the confirmation
    // modal. We never insert until the user clicks "Add Now" in the modal.
    const startAddChannel = async () => {
        const raw = newChannel.trim();
        if (!raw) return;
        setValidationOpen(true);
        setValidationLoading(true);
        setValidationResult(null);
        try {
            const { data } = await api.post<ChannelValidationResult>(
                '/scrapers/telegram/channels/validate',
                { name: raw },
            );
            setValidationResult(data);
        } catch (e: any) {
            console.error('Channel validation failed:', e);
            setValidationResult({
                input: raw,
                normalized: raw,
                valid_format: true,
                exists: null,
                reason: e?.response?.data?.detail || 'Validation request failed.',
            });
        } finally {
            setValidationLoading(false);
        }
    };

    // Step 2: user clicked "Add Now" in the modal — actually persist.
    const confirmAddChannel = async () => {
        const raw = (validationResult?.normalized || newChannel).trim();
        if (!raw) { setValidationOpen(false); return; }
        const visibility = canAddPublic ? newChannelVisibility : 'private';
        try {
            await api.post('/scrapers/telegram/channels', { name: raw, visibility });
            setNewChannel('');
            setValidationOpen(false);
            setValidationResult(null);
            toast.success(visibility === 'private'
                ? `Added @${raw} (private — only you can see it)`
                : `Added @${raw} (public)`);
            fetchChannels();
        } catch (e: any) {
            console.error('Failed to add channel:', e);
            toast.error(e?.response?.data?.detail || 'Failed to add channel');
        }
    };

    const closeValidation = () => {
        setValidationOpen(false);
        setValidationLoading(false);
        setValidationResult(null);
    };

    const deleteChannel = async (name: string) => {
        if (!window.confirm(`Remove channel #${name} from your list?`)) return;
        try {
            await api.delete(`/scrapers/telegram/channels/${encodeURIComponent(name)}`);
            toast.success(`Removed #${name}`);
            fetchChannels();
        } catch (e: any) {
            console.error('Failed to delete channel:', e);
            toast.error(e?.response?.data?.detail || 'Failed to delete channel');
        }
    };

    const handleScopeChange = (scope: MsgScope) => {
        setMsgScope(scope);
        fetchMessages(1, scope, msgSearch, msgRelevantOnly);
    };

    const handleMsgSearchSubmit = () => {
        const trimmed = msgSearch.trim();
        const next = new URLSearchParams(searchParams);
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        setSearchParams(next, { replace: true });
        fetchMessages(1, msgScope, msgSearch, msgRelevantOnly);
    };

    const handleMsgRelevantToggle = (next: boolean) => {
        setMsgRelevantOnly(next);
        fetchMessages(1, msgScope, msgSearch, next);
    };

    const clearScrapePoll = () => {
        if (scrapePollRef.current != null) {
            window.clearInterval(scrapePollRef.current);
            scrapePollRef.current = null;
        }
    };

    const runScrape = async () => {
        clearScrapePoll();
        setScrapeRunning(true); setScrapeStatus('Starting...');
        try {
            const selected = channels.filter(c => c.enabled).map(c => c.name);
            const res = await api.post('/scrapers/telegram/run', { channels: selected, per_channel_limit: scrapeLimit });
            const jobId = res.data.job_id;
            setScrapeJobId(jobId);
            scrapePollRef.current = window.setInterval(async () => {
                try {
                    const s = await api.get(`/scrapers/telegram/run/${jobId}`);
                    const st = s.data.status as string;
                    if (st === 'completed' && s.data.summary?.status !== 'complete') {
                        setScrapeStatus(`${s.data.summary?.status || 'completed'}: ${s.data.summary?.reason || 'Some channels may remain due'}`);
                    } else if (st === 'running') {
                        setScrapeStatus(`Running... ${s.data.progress || ''}`);
                    } else if (st === 'cancelled') {
                        setScrapeStatus('Cancelled');
                    } else {
                        setScrapeStatus(st);
                    }
                    if (st === 'completed' || st === 'error' || st === 'cancelled') {
                        clearScrapePoll();
                        setScrapeRunning(false);
                        setScrapeJobId(null);
                        if (st === 'completed') { fetchMessages(); fetchChannels(); }
                        if (st === 'cancelled') toast.message('Telegram scrape cancelled');
                    }
                } catch { /* keep polling */ }
            }, 3000);
        } catch (e) { console.error('Scrape failed:', e); setScrapeRunning(false); }
    };

    const cancelScrape = async () => {
        if (!scrapeJobId) return;
        try {
            await api.post(`/scrapers/telegram/run/${scrapeJobId}/cancel`);
            setScrapeStatus('Cancelling…');
        } catch (e: any) {
            const detail = e?.response?.data?.detail || e?.message || 'Cancel failed';
            toast.error(typeof detail === 'string' ? detail : 'Cancel failed');
        }
    };

    const runDeepBackfill = async () => {
        setDeepBackfillRunning(true);
        try {
            const r = await api.post('/scrapers/telegram/deep-backfill', null, { params: { limit: 10000 } });
            const jobHint = r.data?.job_id ? ` (job ${String(r.data.job_id).slice(0, 8)})` : '';
            toast.success(`Deep backfill queued${jobHint}`);
            setScrapeStatus('Deep backfill started (background)');
        } catch (e: any) {
            console.error('Deep backfill failed:', e);
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || e?.message || 'Deep backfill failed';
            if (status === 403) {
                toast.error(typeof detail === 'string' ? detail : 'Deep backfill not allowed for your role');
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Deep backfill failed');
            }
        } finally {
            setTimeout(() => setDeepBackfillRunning(false), 5000);
        }
    };

    const runIntelProcessing = async () => {
        setProcessing(true);
        try {
            const res = await api.post('/scrapers/telegram/process-intelligence');
            const poll = setInterval(async () => {
                try {
                    const s = await api.get(`/scrapers/telegram/run/${res.data.job_id}`);
                    if (s.data.status === 'completed' || s.data.status === 'error') {
                        clearInterval(poll); setProcessing(false);
                        if (s.data.status === 'completed') fetchDeepIntel();
                    }
                } catch { /* keep polling */ }
            }, 5000);
        } catch (e) { console.error('Processing failed:', e); setProcessing(false); }
    };

    // ── Tab definitions ────────────────────────────────────────────

    const groups: { id: TabId; label: string; icon: React.ReactNode; members: TabId[] }[] = [
        { id:'deep', label:'Overview', icon:<Eye className="w-4 h-4" />, members:['deep'] },
        { id:'messages', label:'Messages & News', icon:<MessageSquare className="w-4 h-4" />, members:['messages','news','osint'] },
        { id:'entities', label:'Identities', icon:<Users className="w-4 h-4" />, members:['entities','senders','phones'] },
        { id:'connections', label:'Connections', icon:<Network className="w-4 h-4" />, members:['connections'] },
        { id:'channels', label:'Collection & Sources', icon:<Radio className="w-4 h-4" />, members:['channels','exchanges'] },
    ];
    const currentGroup = groups.find(g => g.members.includes(activeTab)) || groups[0];
    const viewLabels: Record<TabId,string> = {
        deep:'Overview', messages:'Messages', news:'News & locations', osint:'Identifier search',
        entities:'Extracted identifiers', senders:'Sender profiles', connections:'Connections',
        channels:'Channels & collection', exchanges:'Provider directory', phones:'Phone inventory',
    };

    const lake = deepData?.lake_totals;

    return (
        <div className="p-6 max-w-[1400px] mx-auto min-w-0 w-full">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-3">
                            <IntelStatIcon icon={MessageSquare} tone="brand" size="lg" />
                            Telegram Intelligence
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            {deepData?.archive ? `${fmtNum(deepData.archive.total_messages)} messages | ${deepData.archive.channels} channels | ${deepData.archive.oldest_message?.slice(0, 10)} to ${deepData.archive.newest_message?.slice(0, 10)}` : 'Multi-channel OSINT and P2P syndicate lake'}
                        </p>
                    </div>
                    <button onClick={() => { fetchChannels(); setRefreshKey(k => k+1); if (activeTab === 'deep') fetchDeepIntel(true); else if (activeTab === 'messages') fetchMessages(); else if (activeTab === 'news') fetchNews(); else if (activeTab === 'entities') fetchEntities(); else if (activeTab === 'senders') fetchIntelSummary(); else if (activeTab === 'exchanges') fetchProviders(); }}
                        className="p-2 hover:bg-muted rounded-lg" title="Refresh" aria-label="Refresh Telegram view">
                        <RefreshCw className={`w-5 h-5 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Harvest lake overview. Every figure is a row count returned by
                /scrapers/telegram/intelligence-summary -- nothing here is derived
                or estimated, so it can be read out as-is. */}
            {lake && (
                <div className="mb-6 p-4 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 via-primary/10 to-transparent shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
                            Harvest intelligence lake
                        </h2>
                        <span className="text-[11px] text-muted-foreground font-mono">
                            {deepData?.archive?.oldest_message
                                ? `${deepData.archive.oldest_message.slice(0, 10)} to ${deepData.archive.newest_message?.slice(0, 10)}`
                                : 'row counts'}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {([
                            { icon: Radio, tone: 'text-blue-500', label: 'Monitored channels', value: lake.channels, hint: 'Registered for scraping' },
                            { icon: MessageSquare, tone: 'text-emerald-500', label: 'P2P escrow messages', value: lake.p2p_messages, hint: 'Trade chatter captured' },
                            { icon: Eye, tone: 'text-indigo-500', label: 'Raw archive', value: lake.raw_archive, hint: `${fmtNum(deepData?.archive?.processed || 0)} NLP-processed` },
                            { icon: AlertTriangle, tone: 'text-red-500', label: 'Flagged scam domains', value: lake.scam_domains, hint: 'Phishing and fake APKs' },
                            { icon: Users, tone: 'text-amber-500', label: 'Cross-channel actors', value: lake.cross_channel_users, hint: 'Posting in 2+ channels' },
                            { icon: Network, tone: 'text-purple-500', label: 'Cross-source hits', value: lake.cross_source_hits, hint: 'Telegram to P2P / entity DB' },
                        ] as const).map(card => (
                            <div key={card.label} className="p-3 bg-card/70 backdrop-blur rounded-lg border border-border/60">
                                <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5">
                                    <card.icon className={`w-3.5 h-3.5 ${card.tone}`} /> {card.label}
                                </div>
                                <div className="text-xl font-bold mt-1 text-foreground">{fmtNum(card.value)}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">{card.hint}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <TelegramCollectionStatus />
            <TelegramTabBar tabs={groups} activeTab={currentGroup.id} onSelect={setActiveTab} />
            {currentGroup.members.length > 1 && <div className="flex flex-wrap gap-2" role="group" aria-label={`${currentGroup.label} views`}>
                {currentGroup.members.map(id => <button key={id} onClick={() => setActiveTab(id)} aria-pressed={activeTab === id}
                    className={`rounded border px-3 py-1.5 text-xs ${activeTab === id ? 'border-primary bg-primary/10' : 'border-border'}`}>
                    {viewLabels[id]}
                </button>)}
            </div>}

            {/* Tab Content */}
            <div className="bg-card border border-border rounded-lg">
                {activeTab === 'channels' && <ChannelsTab channels={channels}
                    currentUserId={currentUserId} canAddPublic={canAddPublic}
                    newChannel={newChannel} setNewChannel={setNewChannel}
                    newChannelVisibility={newChannelVisibility} setNewChannelVisibility={setNewChannelVisibility}
                    addChannel={startAddChannel} deleteChannel={deleteChannel}
                    runScrape={runScrape} cancelScrape={cancelScrape}
                    scrapeRunning={scrapeRunning} scrapeStatus={scrapeStatus}
                    scrapeLimit={scrapeLimit} setScrapeLimit={setScrapeLimit}
                    runDeepBackfill={runDeepBackfill} deepBackfillRunning={deepBackfillRunning} />}
                {activeTab === 'messages' && <MessagesTab messages={messages} total={msgTotal} page={msgPage}
                    onPageChange={(p: number) => fetchMessages(p, msgScope, msgSearch, msgRelevantOnly)}
                    scope={msgScope} onScopeChange={handleScopeChange}
                    search={msgSearch} setSearch={setMsgSearch}
                    onSearchSubmit={handleMsgSearchSubmit}
                    relevantOnly={msgRelevantOnly} onRelevantOnlyChange={handleMsgRelevantToggle}
                    channelNames={channels.map(c => c.name)}
                    filteredChannel={msgFilteredChannel}
                    channelScrapeStatus={msgChannelScrapeStatus}
                    onClearChannelFilter={clearChannelFilter}
                    loading={loading} navigate={navigate} />}
                {activeTab === 'news' && <NewsTab
                    error={newsError} pending={deepData?.archive ? Math.max(0, deepData.archive.total_messages-deepData.archive.processed) : undefined}
                    state={newsState} country={newsCountry}
                    onStateChange={setNewsState} onCountryChange={setNewsCountry}
                    items={news} total={newsTotal} page={newsPage}
                    onPageChange={(p: number) => fetchNews(p, newsScope, newsCategory, newsTopic, newsSearch)}
                    scope={newsScope}
                    onScopeChange={(s: MsgScope) => { setNewsScope(s); fetchNews(1, s, newsCategory, newsTopic, newsSearch); }}
                    category={newsCategory}
                    onCategoryChange={(c: NewsCategory) => { setNewsCategory(c); fetchNews(1, newsScope, c, newsTopic, newsSearch); }}
                    topic={newsTopic}
                    onTopicChange={(t: string) => { setNewsTopic(t); fetchNews(1, newsScope, newsCategory, t, newsSearch); }}
                    search={newsSearch} setSearch={setNewsSearch}
                    onSearchSubmit={() => fetchNews(1, newsScope, newsCategory, newsTopic, newsSearch)}
                    channelNames={channels.map(c => c.name)}
                    loading={loading} navigate={navigate}
                />}
                {activeTab === 'entities' && <EntitiesTab
                    entities={entities}
                    total={entitiesTotal}
                    loading={loading}
                    entityType={entityType}
                    scope={entityScope}
                    channel={entityChannel}
                    days={entityDays}
                    search={entitySearch}
                    channelNames={channels.map(c => c.name)}
                    setSearch={setEntitySearch}
                    onTypeChange={(t: EntityType) => { setEntityType(t); fetchEntities(t, entityScope, entityChannel, entityDays, entitySearch); }}
                    onScopeChange={(s: MsgScope) => { setEntityScope(s); fetchEntities(entityType, s, entityChannel, entityDays, entitySearch); }}
                    onChannelChange={(c: string) => { setEntityChannel(c); fetchEntities(entityType, entityScope, c, entityDays, entitySearch); }}
                    onDaysChange={(d: number) => { setEntityDays(d); fetchEntities(entityType, entityScope, entityChannel, d, entitySearch); }}
                    onSearchSubmit={() => fetchEntities(entityType, entityScope, entityChannel, entityDays, entitySearch)}
                    onDrill={(value: string) => openEntityDrilldown(entityType, value)}
                />}
                {activeTab === 'senders' && <SendersTab data={intelSummary} loading={loading} navigate={navigate} />}
                {activeTab === 'osint' && <OSINTTab navigate={navigate} />}
                {activeTab === 'connections' && <TelegramConnections refreshKey={refreshKey} />}
                {activeTab === 'phones' && <div className="p-4"><PhoneInventoryPanel key={refreshKey} expanded /></div>}
                {activeTab === 'deep' && <DeepAnalysisTab data={deepData} loading={loading} processing={processing}
                    runProcessing={runIntelProcessing} expandedSection={expandedSection}
                    setExpandedSection={setExpandedSection} navigate={navigate} />}
                {activeTab === 'exchanges' && <ExchangesTab
                    providers={providers}
                    selectedCategories={providerCategories}
                    setSelectedCategories={setProviderCategories}
                    search={providerSearch} setSearch={setProviderSearch}
                    expandedId={expandedProviderId} setExpandedId={setExpandedProviderId}
                    canBackfill={canAddPublic}
                    onBackfillChannel={async (handle) => {
                        if (!confirm(`Deep-backfill @${handle}? This pulls full channel history (up to 50,000 messages), may take several minutes, and is heavy on the Telegram session quota.`)) return;
                        try {
                            const r = await api.post('/scrapers/telegram/deep-backfill', null, {
                                params: { channels: handle, limit: 50000 },
                            });
                            toast.success(`Backfill queued for @${handle} (job ${r.data.job_id?.slice(0, 8)})`);
                        } catch (e: any) {
                            const status = e?.response?.status;
                            const detail = e?.response?.data?.detail || e.message;
                            toast.error(
                                status === 403
                                    ? (typeof detail === 'string' ? detail : 'Deep backfill not allowed for your role')
                                    : `Backfill failed: ${detail}`,
                            );
                        }
                    }}
                    onJumpToMessages={(handle) => {
                        applyChannelFilter(handle);
                    }}
                />}
            </div>

            <ChannelValidationModal
                open={validationOpen}
                loading={validationLoading}
                result={validationResult}
                onConfirm={confirmAddChannel}
                onRetry={closeValidation}
                onCancel={closeValidation}
            />

            {/* Entity drill-down modal — appears when an aggregated
                entity row is clicked. Lists the raw_archive messages
                containing that value, with the same expandable row
                treatment as the Messages/News tabs. */}
            {entityDrill && (
                <EntityDrilldownModal
                    entityType={entityDrill.type}
                    value={entityDrill.value}
                    messages={entityDrill.messages}
                    total={entityDrill.total}
                    loading={entityDrill.loading}
                    onClose={() => setEntityDrill(null)}
                    navigate={navigate}
                />
            )}
        </div>
    );
}

// ─── Entities tab + drill-down modal ─────────────────────────────

const ENTITY_TYPE_META: Record<EntityType, { label: string; hint: string; icon: React.ReactNode }> = {
    domains: { label: 'Domains', hint: 'HTTP hosts extracted from message links (e.g. broker-qx.pro)', icon: <Globe className="w-3.5 h-3.5" /> },
    urls: { label: 'URLs (full)', hint: 'Every href pulled out of a message body', icon: <Link2 className="w-3.5 h-3.5" /> },
    onion_domains: { label: 'Onion (.onion)', hint: 'Darknet domains — high-signal indicator', icon: <Lock className="w-3.5 h-3.5" /> },
    tg_group_refs: { label: '@handles / t.me refs', hint: 'Telegram handles + t.me/… links other channels pointed to', icon: <MessageSquare className="w-3.5 h-3.5" /> },
    upi_ids: { label: 'UPI IDs', hint: 'Payment handles like something@paytm', icon: <Hash className="w-3.5 h-3.5" /> },
    crypto_addresses: { label: 'Wallets', hint: 'BTC/ETH/TRX/XMR addresses extracted from body', icon: <Hash className="w-3.5 h-3.5" /> },
    tx_hashes: { label: 'Tx hashes', hint: '64-hex transaction hashes', icon: <Hash className="w-3.5 h-3.5" /> },
    mentioned_services: { label: 'Services mentioned', hint: 'Exchange / mixer / swap brands named in messages', icon: <Building2 className="w-3.5 h-3.5" /> },
};

function EntitiesTab({
    entities, total, loading,
    entityType, scope, channel, days, search,
    channelNames, setSearch,
    onTypeChange, onScopeChange, onChannelChange, onDaysChange, onSearchSubmit,
    onDrill,
}: {
    entities: AggregatedEntity[]; total: number; loading: boolean;
    entityType: EntityType; scope: MsgScope; channel: string; days: number; search: string;
    channelNames: string[];
    setSearch: (v: string) => void;
    onTypeChange: (t: EntityType) => void;
    onScopeChange: (s: MsgScope) => void;
    onChannelChange: (c: string) => void;
    onDaysChange: (d: number) => void;
    onSearchSubmit: () => void;
    onDrill: (value: string) => void;
}) {
    return (
        <div className="p-6 space-y-4">
            {/* Entity type pills */}
            <div className="flex items-center gap-1.5 text-xs flex-wrap">
                <span className="text-muted-foreground">Show:</span>
                {(Object.keys(ENTITY_TYPE_META) as EntityType[]).map(t => (
                    <button key={t} onClick={() => onTypeChange(t)}
                        title={ENTITY_TYPE_META[t].hint}
                        className={`px-2.5 py-1 rounded border transition-colors flex items-center gap-1 ${
                            entityType === t
                                ? 'border-primary/50 bg-primary/15 text-foreground font-medium'
                                : 'border-border text-muted-foreground hover:bg-secondary'
                        }`}>
                        {ENTITY_TYPE_META[t].icon}
                        {ENTITY_TYPE_META[t].label}
                    </button>
                ))}
            </div>

            {/* Filter row */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 relative min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') onSearchSubmit(); }}
                        placeholder="Substring match on entity value (e.g. paytm, broker-qx)…"
                        className="w-full pl-10 pr-3 py-2 text-sm bg-secondary/50 border border-border rounded" />
                </div>
                <button onClick={onSearchSubmit}
                    className="px-3 py-2 text-xs bg-primary text-primary-foreground font-medium rounded hover:bg-brand-gold-hover transition-colors shadow-sm">
                    Search
                </button>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-muted-foreground">Scope:</span>
                    {(Object.keys(SCOPE_LABELS) as MsgScope[]).map(s => (
                        <button key={s} onClick={() => onScopeChange(s)}
                            className={`px-2.5 py-1 rounded border transition-colors ${
                                scope === s
                                    ? 'border-primary/50 bg-primary/15 text-foreground font-medium'
                                    : 'border-border text-muted-foreground hover:bg-secondary'
                            }`}>
                            {SCOPE_LABELS[s]}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-muted-foreground">Channel:</span>
                    <select value={channel} onChange={e => onChannelChange(e.target.value)}
                        className="px-2 py-1 rounded bg-secondary/50 border border-border text-xs min-w-[180px]">
                        <option value="">All I can see</option>
                        {channelNames.map(c => <option key={c} value={c}>#{c}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-muted-foreground">Window:</span>
                    <select value={days} onChange={e => onDaysChange(Number(e.target.value))}
                        className="px-2 py-1 rounded bg-secondary/50 border border-border text-xs">
                        <option value={7}>7d</option><option value={30}>30d</option>
                        <option value={90}>90d</option><option value={180}>180d</option>
                        <option value={365}>365d</option>
                    </select>
                </div>
                <span className="text-xs text-muted-foreground ml-auto">
                    {loading ? 'Loading…' : `${fmtNum(entities.length)} of ${fmtNum(total)} distinct`}
                </span>
            </div>

            {/* Aggregated results table */}
            <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-secondary/30 text-muted-foreground uppercase tracking-wide text-[10px]">
                        <tr>
                            <th className="px-3 py-2 text-left">{ENTITY_TYPE_META[entityType].label}</th>
                            <th className="px-3 py-2 text-right w-16">Hits</th>
                            <th className="px-3 py-2 text-left">Channels</th>
                            <th className="px-3 py-2 text-left w-32">First seen</th>
                            <th className="px-3 py-2 text-left w-32">Last seen</th>
                            <th className="px-3 py-2 text-right w-24">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entities.length === 0 && !loading && (
                            <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">
                                No {ENTITY_TYPE_META[entityType].label.toLowerCase()} extracted in this window. Try widening the time window or a different entity type.
                            </td></tr>
                        )}
                        {entities.map((e) => (
                            <tr key={e.value} className="border-t border-border hover:bg-muted/30">
                                <td className="px-3 py-2 font-mono break-all max-w-[360px]">
                                    <button onClick={() => onDrill(e.value)}
                                        className="text-left hover:underline text-foreground/90">
                                        {e.value}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtNum(e.hits)}</td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap gap-1">
                                        {e.channels.slice(0, 3).map(c => (
                                            <span key={c} className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-foreground font-medium rounded border border-primary/20">#{c}</span>
                                        ))}
                                        {e.channels.length > 3 && (
                                            <span className="text-[10px] text-muted-foreground">+{e.channels.length - 3}</span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtDate(e.first_seen)}</td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtDate(e.last_seen)}</td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex justify-end gap-1">
                                        <button onClick={() => navigator.clipboard.writeText(e.value)}
                                            className="p-1 hover:bg-muted rounded" title="Copy value">
                                            <Copy className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => onDrill(e.value)}
                                            className="text-[10px] px-2 py-0.5 bg-primary/15 text-foreground border border-primary/30 rounded hover:bg-primary/25 font-medium">
                                            {fmtNum(e.hits)} msg
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EntityDrilldownModal({
    entityType, value, messages, total, loading, onClose, navigate,
}: {
    entityType: EntityType; value: string;
    messages: TelegramMessage[]; total: number; loading: boolean;
    onClose: () => void; navigate: any;
}) {
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={onClose}>
            <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">
                            {ENTITY_TYPE_META[entityType].label}
                        </div>
                        <div className="text-sm font-mono break-all">{value}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                            {loading ? 'Loading…' : `${fmtNum(total)} source message${total === 1 ? '' : 's'}`}
                        </span>
                        <button onClick={onClose} className="p-1 hover:bg-muted rounded">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {loading && (
                        <p className="text-sm text-muted-foreground text-center py-8">Loading source messages…</p>
                    )}
                    {!loading && messages.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-8">
                            No source messages found (may have been pruned from retention).
                        </p>
                    )}
                    {messages.map((m, i) => (
                        <MessageRow key={`${m.channel}-${m.message_id ?? m.id}-${i}`} m={m} navigate={navigate} />
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── Channels Tab ──────────────────────────────────────────────────

const PRIVACY_CHANNEL_NAMES = new Set([
    'monero','moneromarket','localmonero','MoneroCommunity','zcashcommunity',
    'DashDigitalCash','fixedfloat','ChangeNOW_io','cakewallet',
    'xmr_india','crypto_india_xmr',
]);

function ChannelsTab({
    channels, currentUserId, canAddPublic,
    newChannel, setNewChannel, newChannelVisibility, setNewChannelVisibility,
    addChannel, deleteChannel,
    runScrape, cancelScrape, scrapeRunning, scrapeStatus,
    scrapeLimit, setScrapeLimit, runDeepBackfill, deepBackfillRunning,
}: {
    channels: Channel[]; currentUserId: number | null; canAddPublic: boolean;
    newChannel: string; setNewChannel: (v: string) => void;
    newChannelVisibility: Visibility; setNewChannelVisibility: (v: Visibility) => void;
    addChannel: () => void; deleteChannel: (name: string) => void;
    runScrape: () => void; cancelScrape: () => void; scrapeRunning: boolean; scrapeStatus: string;
    scrapeLimit: number; setScrapeLimit: (n: number) => void;
    runDeepBackfill: () => void; deepBackfillRunning: boolean;
}) {
    // Group by source/visibility/ownership.
    // FIU-IND seed (public, source='fiu-ind') is rendered separately so officers
    // can see at a glance which exchanges they're monitoring. The "subordinate"
    // bucket only renders when the current user (admin) sees a private channel
    // they don't own — i.e. one created by an officer they manage.
    const quarantined = channels.filter(c => c.health_status === 'quarantined' || (!c.enabled && Boolean(c.quarantine_reason)));
    const active = channels.filter(c => c.health_status !== 'quarantined' && c.enabled);

    const fiuInd = active.filter(c => c.visibility === 'public' && c.source === 'fiu-ind');
    const otherPublic = active.filter(c => c.visibility === 'public' && c.source !== 'fiu-ind');
    const myPrivate = active.filter(c => c.visibility === 'private' && c.owner_user_id === currentUserId);
    const subordinatePrivate = active.filter(c => c.visibility === 'private' && c.owner_user_id !== currentUserId);

    const privacyPublic = otherPublic.filter(c => PRIVACY_CHANNEL_NAMES.has(c.name));
    const p2pPublic = otherPublic.filter(c => !PRIVACY_CHANNEL_NAMES.has(c.name));

    const canDelete = (c: Channel) => {
        if (c.visibility === 'public') return canAddPublic; // admins only
        return c.owner_user_id === currentUserId || canAddPublic; // owner or admin
    };

    return (
        <div className="p-6 space-y-6">
            {/* Add row */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[280px] flex items-center gap-2">
                    <input value={newChannel} onChange={e => setNewChannel(e.target.value)} placeholder="Add channel username (e.g. WazirX)..."
                        className="flex-1 px-3 py-2 text-sm bg-secondary/50 border border-border rounded"
                        onKeyDown={e => e.key === 'Enter' && addChannel()} />
                    {canAddPublic && (
                        <select value={newChannelVisibility} onChange={e => setNewChannelVisibility(e.target.value as Visibility)}
                            className="px-2 py-2 text-sm bg-secondary/50 border border-border rounded"
                            title="Public = visible to all officers; Private = only you (and your admin) can see it">
                            <option value="private">Private</option>
                            <option value="public">Public</option>
                        </select>
                    )}
                    <button onClick={addChannel} className="px-3.5 py-2 text-sm bg-primary text-primary-foreground font-medium rounded hover:bg-brand-gold-hover flex items-center gap-1 shadow-sm transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                </div>
                <select value={scrapeLimit} onChange={e => setScrapeLimit(Number(e.target.value))}
                    className="px-2 py-2 text-sm bg-secondary/50 border border-border rounded">
                    <option value={50}>50/ch</option><option value={100}>100/ch</option><option value={500}>500/ch</option><option value={1000}>1K/ch</option>
                </select>
                <button onClick={runScrape} disabled={scrapeRunning}
                    className="px-4 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 dark:hover:bg-emerald-500/30 disabled:opacity-50 rounded font-medium flex items-center gap-1 shadow-sm transition-colors">
                    <Play className="w-3.5 h-3.5" /> {scrapeRunning ? 'Running...' : 'Run Scrape'}
                </button>
                {scrapeRunning && (
                    <button
                        type="button"
                        onClick={cancelScrape}
                        className="px-4 py-2 text-sm bg-muted text-muted-foreground border border-border rounded hover:bg-muted/80 flex items-center gap-1"
                    >
                        <X className="w-3 h-3" /> Cancel
                    </button>
                )}
                <button onClick={runDeepBackfill} disabled={deepBackfillRunning}
                    className={`${brandPrimaryBtnMd} disabled:opacity-50 flex items-center gap-1`}>
                    <Eye className="w-3 h-3" /> {deepBackfillRunning ? 'Starting...' : 'Deep Backfill'}
                </button>
            </div>
            {!canAddPublic && (
                <p className="text-xs text-muted-foreground px-1">
                    Channels you add are <strong>private</strong> — only you (and your admin) can see them or the messages scraped from them.
                </p>
            )}
            {scrapeStatus && <div className="text-xs text-muted-foreground px-1">{scrapeStatus}</div>}

            {/* Channel groups */}
            <div className="space-y-5">
                {fiuInd.length > 0 && (
                    <ChannelGroup
                        title="FIU-IND Registered Exchanges"
                        subtitle="Public · curated list of exchanges registered with FIU-IND (visible to all officers)"
                        color="text-emerald-700 dark:text-emerald-400"
                        channels={fiuInd}
                        canDelete={canDelete}
                        onDelete={deleteChannel}
                    />
                )}
                {p2pPublic.length > 0 && (
                    <ChannelGroup
                        title="Indian P2P & USDT (Public)"
                        color="text-foreground"
                        channels={p2pPublic}
                        canDelete={canDelete}
                        onDelete={deleteChannel}
                    />
                )}
                {privacyPublic.length > 0 && (
                    <ChannelGroup
                        title="Privacy Chain (Public)"
                        color="text-violet-700 dark:text-violet-400"
                        channels={privacyPublic}
                        canDelete={canDelete}
                        onDelete={deleteChannel}
                    />
                )}
                <ChannelGroup
                    title="My Private Channels"
                    subtitle="Only you (and your admin) can see these and their scraped messages"
                    color="text-amber-700 dark:text-amber-400"
                    channels={myPrivate}
                    canDelete={canDelete}
                    onDelete={deleteChannel}
                    emptyHint="No private channels yet — add one above to monitor it privately."
                />
                {subordinatePrivate.length > 0 && (
                    <ChannelGroup
                        title="Officers' Private Channels"
                        subtitle="Private channels added by officers you manage"
                        color="text-cyan-700 dark:text-cyan-400"
                        channels={subordinatePrivate}
                        canDelete={canDelete}
                        onDelete={deleteChannel}
                    />
                )}
                {quarantined.length > 0 && (
                    <ChannelGroup
                        title="Quarantined / Dead Channels"
                        subtitle={`Auto-quarantined to protect Telegram scraper accounts from abuse bans (${quarantined.length} dead/invalid handles isolated)`}
                        color="text-rose-700 dark:text-rose-400"
                        channels={quarantined}
                        canDelete={canDelete}
                        onDelete={deleteChannel}
                    />
                )}
            </div>
        </div>
    );
}

function ChannelGroup({ title, subtitle, color, channels, canDelete, onDelete, emptyHint }: {
    title: string; subtitle?: string; color: string; channels: Channel[];
    canDelete: (c: Channel) => boolean; onDelete: (name: string) => void; emptyHint?: string;
}) {
    return (
        <div>
            <div className="mb-2">
                <h3 className={`text-sm font-semibold ${color}`}>{title} ({channels.length})</h3>
                {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            {channels.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">{emptyHint || 'None.'}</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {channels.map(c => (
                        <ChannelChip key={c.name} channel={c}
                            canDelete={canDelete(c)} onDelete={() => onDelete(c.name)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ChannelChip({ channel, canDelete, onDelete }: { channel: Channel; canDelete: boolean; onDelete: () => void }) {
    const isPrivate = channel.visibility === 'private';
    const isQuarantined = channel.health_status === 'quarantined' || (!channel.enabled && Boolean(channel.quarantine_reason));
    const scrapeLabel = isQuarantined 
        ? `Quarantined: ${channel.quarantine_reason || 'Dead/invalid username'}` 
        : formatScrapeAttemptStatus(channel.last_scrape_attempt);

    return (
        <div className={`px-3 py-2 rounded border text-sm flex flex-col gap-1 transition-colors ${
            isQuarantined
                ? 'border-rose-400/50 bg-rose-500/10 text-rose-950 dark:text-rose-100'
                : channel.enabled
                    ? 'border-border bg-card shadow-sm hover:border-primary/40'
                    : 'border-border bg-muted/30 opacity-60'
        }`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                    {isPrivate && (
                        <span title="Private — visible only to you and your admin" className="shrink-0">
                            <Lock className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                        </span>
                    )}
                    {isQuarantined && (
                        <span title={`Auto-quarantined: ${channel.quarantine_reason || 'Dead/invalid handle'}`} className="shrink-0">
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                        </span>
                    )}
                    <span className="truncate font-medium text-foreground">#{channel.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {isPrivate && channel.owner_username && (
                        <span className="text-[10px] text-muted-foreground" title={`Owner: ${channel.owner_username}`}>
                            {channel.owner_username}
                        </span>
                    )}
                    {canDelete && (
                        <button onClick={onDelete} className="p-0.5 hover:bg-red-500/15 rounded text-muted-foreground hover:text-red-600 dark:hover:text-red-400" title="Remove channel">
                            <Trash2 className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>
            <div
                className={`text-[10px] truncate ${
                    isQuarantined
                        ? 'text-rose-700 dark:text-rose-400 font-medium'
                        : channel.last_scrape_attempt?.error_type
                            ? 'text-amber-700 dark:text-amber-400 font-medium'
                            : channel.last_scrape_attempt && channel.last_scrape_attempt.msgs_pulled === 0
                                ? 'text-amber-700 dark:text-amber-400'
                                : 'text-muted-foreground'
                }`}
                title={scrapeLabel}
            >
                {scrapeLabel}
            </div>
        </div>
    );
}

// ─── Messages Tab ──────────────────────────────────────────────────

// Shared truncation limit for message previews. Below this, we show the
// full text; above it, we clip at CONTENT_PREVIEW_CHARS + "Show more"
// button that expands to the full body with internal scroll for the
// really long screeds. Pre-Jun-11 2026 both message tabs had a hard
// max-h-24 with overflow-hidden and no signal at all — long messages
// just vanished after ~4 lines.
const CONTENT_PREVIEW_CHARS = 280;

function MessageRow({ m, navigate, showChannelChip = true }: {
    m: TelegramMessage; navigate: any; showChannelChip?: boolean;
}) {
    const [expanded, setExpanded] = React.useState(false);
    const isLong = (m.content || '').length > CONTENT_PREVIEW_CHARS;
    const displayText = expanded || !isLong
        ? m.content
        : m.content.substring(0, CONTENT_PREVIEW_CHARS) + '…';
    const urls = m.extracted_urls || [];
    const domains = m.extracted_domains || [];
    const topicTags = m.topic_tags || [];
    const tgRefs = m.tg_group_refs || [];
    const txs = m.tx_hashes || [];

    return (
        <div className="p-3 bg-secondary/20 rounded border border-border">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
                {showChannelChip && (
                    <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-foreground font-medium rounded border border-primary/20">#{m.channel}</span>
                )}
                <span className="text-xs font-medium">{m.sender}</span>
                {m.has_media && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-800 dark:text-amber-300 rounded border border-amber-500/30"
                        title={m.media_type ? `Media: ${m.media_type} (only the caption text is stored)` : 'Media attached (only caption text is stored)'}>
                        📎 {m.media_type ? m.media_type.replace('MessageMedia', '').toLowerCase() : 'media'}
                    </span>
                )}
                {m.is_relevant === false && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded" title="Classifier flagged as non-P2P — you're seeing it because 'Relevant only' is unticked or you're on the News tab.">
                        news
                    </span>
                )}
                <span className="text-xs text-muted-foreground ml-auto">{fmtDate(m.scraped_at)}</span>
            </div>
            <p className={`text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap ${expanded ? 'max-h-96 overflow-y-auto pr-1' : ''}`}>
                {displayText || <em className="text-muted-foreground/60">(empty — likely a media-only message; no caption)</em>}
            </p>
            {isLong && (
                <button onClick={() => setExpanded(v => !v)}
                    className="mt-1 text-[10px] text-amber-800 dark:text-amber-400 font-medium hover:underline">
                    {expanded ? 'Show less' : `Show more (${(m.content?.length ?? 0).toLocaleString()} chars)`}
                </button>
            )}
            {(topicTags.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {topicTags.map((tg: string) => (
                        <span key={'t-'+tg} className={`text-[10px] px-1.5 py-0.5 rounded ${TOPIC_COLORS[tg] || 'bg-muted text-foreground/70'}`}>
                            {tg}
                        </span>
                    ))}
                </div>
            )}
            {(m.upi_ids?.length > 0 || m.crypto_addresses?.length > 0 || txs.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {m.upi_ids?.map((u: string) => (
                        <span key={'u-'+u} className="text-[10px] px-1.5 py-0.5 bg-green-500/15 text-green-700 dark:text-green-400 rounded font-mono">{u}</span>
                    ))}
                    {m.crypto_addresses?.map((a: string) => (
                        <button key={'w-'+a} onClick={() => navigate(`/workbench?address=${a}`)}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-mono hover:bg-primary/20 ${brandChipBrand}`}
                            title={a}>
                            {a.slice(0, 10)}…
                        </button>
                    ))}
                    {txs.map((h: string) => (
                        <span key={'x-'+h} className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-foreground rounded font-mono border border-primary/20" title={h}>
                            tx:{h.slice(0, 10)}…
                        </span>
                    ))}
                </div>
            )}
            {(urls.length > 0 || domains.length > 0 || tgRefs.length > 0) && (
                <div className="flex flex-wrap gap-1 mt-1.5 text-[10px]">
                    {domains.slice(0, 6).map((d: string) => (
                        <a key={'d-'+d} href={`https://${d}`} target="_blank" rel="noopener noreferrer"
                            className="px-1.5 py-0.5 bg-orange-500/15 text-orange-700 dark:text-orange-400 rounded hover:bg-orange-500/25 font-mono border border-orange-500/30"
                            title={`Domain extracted from message body: ${d}`}>
                            🌐 {d}
                        </a>
                    ))}
                    {urls.length > domains.length && (
                        <span className="px-1.5 py-0.5 bg-muted text-muted-foreground rounded"
                            title={urls.join('\n')}>
                            +{urls.length - domains.length} raw url{urls.length - domains.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {tgRefs.slice(0, 4).map((h: string) => (
                        <span key={'tg-'+h} className="px-1.5 py-0.5 bg-sky-500/15 text-sky-700 dark:text-sky-400 rounded font-mono border border-sky-500/30">
                            @{h.replace(/^@/, '')}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

const SCOPE_LABELS: Record<MsgScope, string> = {
    'all': 'All visible',
    'public': 'Public only',
    'fiu-ind': 'FIU-IND only',
    'private': 'My private only',
};

function MessagesTab({
    messages, total, page, onPageChange, scope, onScopeChange, search, setSearch, onSearchSubmit,
    relevantOnly, onRelevantOnlyChange, channelNames, filteredChannel, channelScrapeStatus,
    onClearChannelFilter, loading, navigate,
}: {
    messages: TelegramMessage[]; total: number; page: number;
    onPageChange: (p: number) => void;
    scope: MsgScope; onScopeChange: (s: MsgScope) => void;
    search: string; setSearch: (v: string) => void;
    onSearchSubmit: () => void;
    relevantOnly: boolean; onRelevantOnlyChange: (next: boolean) => void;
    channelNames: string[];
    filteredChannel: string | null;
    channelScrapeStatus: ScrapeAttempt | null;
    onClearChannelFilter: () => void;
    loading: boolean; navigate: any;
}) {
    const totalPages = Math.ceil(total / 30);
    // Live channel-name match preview. If the typed text exactly equals
    // (case-insensitive) one of the channels the user can see, we hint
    // that hitting Enter will filter by that channel rather than running
    // a substring search.
    const trimmed = search.trim();
    const channelMatch = trimmed
        ? channelNames.find(n => n.toLowerCase() === trimmed.toLowerCase())
        : null;
    // Prefer backend-resolved filtered_channel; fall back to exact name match in search box / URL.
    const chipChannel = filteredChannel
        || (trimmed && channelNames.find(n => n.toLowerCase() === trimmed.toLowerCase()))
        || null;

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 relative min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') onSearchSubmit(); }}
                        placeholder="Search messages or type a channel name…"
                        className="w-full pl-10 pr-3 py-2 text-sm bg-secondary/50 border border-border rounded" />
                </div>
                <button onClick={onSearchSubmit}
                    className="px-3 py-2 text-xs bg-primary text-primary-foreground font-medium rounded hover:bg-brand-gold-hover transition-colors shadow-sm">
                    Search
                </button>
                <label className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-border bg-secondary/40 cursor-pointer"
                    title="When ticked, show only messages the relevance classifier flagged as P2P/trade-related. Unticked shows everything that landed in the data lake — useful for OSINT or non-P2P watchlist channels.">
                    <input type="checkbox" checked={relevantOnly} onChange={e => onRelevantOnlyChange(e.target.checked)} />
                    Relevant only
                </label>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-muted-foreground">Scope:</span>
                    {(Object.keys(SCOPE_LABELS) as MsgScope[]).map(s => (
                        <button key={s} onClick={() => onScopeChange(s)}
                            className={`px-2.5 py-1 rounded border transition-colors ${
                                scope === s
                                    ? 'border-primary/50 bg-primary/15 text-foreground font-medium'
                                    : 'border-border text-muted-foreground hover:bg-secondary'
                            }`}>
                            {SCOPE_LABELS[s]}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground">{fmtNum(total)} messages</span>
            </div>
            {chipChannel && (
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        data-testid="channel-filter-chip"
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-primary/40 bg-primary/15 text-foreground font-medium"
                    >
                        Filtered by channel #{chipChannel}
                        <button
                            type="button"
                            aria-label="Clear channel filter"
                            onClick={onClearChannelFilter}
                            className="p-0.5 rounded hover:bg-primary/20 text-foreground/80"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                </div>
            )}
            {channelMatch && !chipChannel && (
                <div className="text-[11px] text-muted-foreground -mt-2">
                    ↩ Enter to filter by channel <span className="text-foreground font-semibold">#{channelMatch}</span>
                </div>
            )}

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {messages.map((m: TelegramMessage, i: number) => (
                    <MessageRow key={`${m.channel}-${m.message_id ?? m.id}-${i}`} m={m} navigate={navigate} />
                ))}
                {messages.length === 0 && !loading && (
                    <div className="text-sm text-muted-foreground text-center py-8 space-y-2 px-4">
                        <p>No messages found.</p>
                        {chipChannel && (
                            <p data-testid="zero-message-diagnostic" className="text-xs text-amber-700 dark:text-amber-400 max-w-xl mx-auto font-medium">
                                {zeroMessageDiagnostic(chipChannel, channelScrapeStatus)}
                            </p>
                        )}
                    </div>
                )}
                {loading && <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>}
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="px-3 py-1 text-xs border border-border rounded disabled:opacity-30">Prev</button>
                    <span className="text-xs text-muted-foreground">Page {page} / {totalPages}</span>
                    <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="px-3 py-1 text-xs border border-border rounded disabled:opacity-30">Next</button>
                </div>
            )}
        </div>
    );
}

// Long-message renderer for the News tab. Same expand-on-click UX as
// MessageRow, but consumed as an inline body (the surrounding row on
// News already has channel + sender + media badges).
function NewsMessageContent({ content }: { content: string }) {
    const [expanded, setExpanded] = React.useState(false);
    const isLong = content.length > CONTENT_PREVIEW_CHARS;
    const displayText = expanded || !isLong ? content : content.substring(0, CONTENT_PREVIEW_CHARS) + '…';
    return (
        <>
            <p className={`text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap ${expanded ? 'max-h-96 overflow-y-auto pr-1' : ''}`}>
                {displayText || <em className="text-muted-foreground/60">(empty — media-only, no caption)</em>}
            </p>
            {isLong && (
                <button onClick={() => setExpanded(v => !v)}
                    className="mt-1 text-[10px] text-amber-800 dark:text-amber-400 font-medium hover:underline">
                    {expanded ? 'Show less' : `Show more (${content.length.toLocaleString()} chars)`}
                </button>
            )}
        </>
    );
}

// ─── News & Intel Tab ──────────────────────────────────────────────
// Queries telegram_raw_archive — every scraped message, including the
// non-P2P ones the filtered Messages tab can't show.

const INDIAN_STATE_OPTIONS = ['Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'];

const NEWS_CATEGORY_LABELS: Record<NewsCategory, string> = {
    news: 'Regulatory, scam and victim reports',
    p2p: 'P2P signals only',
    all: 'All messages',
};

// Topic tags populated by process_raw_archive (see analysis/telegram_intelligence.py).
const NEWS_TOPIC_CHIPS: Array<{ id: string; label: string }> = [
    { id: 'scam_report', label: 'Scam reports' },
    { id: 'victim_report', label: 'Victim reports' },
    { id: 'regulatory', label: 'Regulatory' },
    { id: 'darknet', label: 'Darknet' },
    { id: 'mixer_discussion', label: 'Mixers' },
    { id: 'privacy_tech', label: 'Privacy tech' },
    { id: 'price_discussion', label: 'Price talk' },
    { id: 'marketplace', label: 'Marketplace' },
];

function NewsTab({
    items, total, page, onPageChange,
    state, country, onStateChange, onCountryChange, error, pending,
    scope, onScopeChange,
    category, onCategoryChange,
    topic, onTopicChange,
    search, setSearch, onSearchSubmit,
    channelNames,
    loading, navigate,
}: {
    error: string; pending?: number;
    state: string; country: string; onStateChange: (value: string) => void; onCountryChange: (value: string) => void;
    items: NewsMessage[]; total: number; page: number;
    onPageChange: (p: number) => void;
    scope: MsgScope; onScopeChange: (s: MsgScope) => void;
    category: NewsCategory; onCategoryChange: (c: NewsCategory) => void;
    topic: string; onTopicChange: (t: string) => void;
    search: string; setSearch: (v: string) => void; onSearchSubmit: () => void;
    channelNames: string[];
    loading: boolean; navigate: any;
}) {
    const totalPages = Math.max(1, Math.ceil(total / 30));
    const trimmed = search.trim();
    const channelMatch = trimmed
        ? channelNames.find(n => n.toLowerCase() === trimmed.toLowerCase())
        : null;
    return (
        <div className="p-6 space-y-4">
            {/* Search + scope */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 relative min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') onSearchSubmit(); }}
                        placeholder="Search content or type a channel name…"
                        className="w-full pl-10 pr-3 py-2 text-sm bg-secondary/50 border border-border rounded"
                    />
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-muted-foreground">Scope:</span>
                    {(Object.keys(SCOPE_LABELS) as MsgScope[]).map(s => (
                        <button key={s} onClick={() => onScopeChange(s)}
                            className={`px-2.5 py-1 rounded border transition-colors ${
                                scope === s
                                    ? 'border-primary/50 bg-primary/15 text-foreground font-medium'
                                    : 'border-border text-muted-foreground hover:bg-secondary'
                            }`}>
                            {SCOPE_LABELS[s]}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground">{error ? 'Count unavailable' : `${fmtNum(total)} messages`}</span>
            </div>
            {channelMatch && (
                <div className="text-[11px] text-muted-foreground -mt-2">
                    ↩ Enter to filter by channel <span className="text-foreground font-semibold">#{channelMatch}</span>
                </div>
            )}

            <div className="flex flex-wrap gap-2 text-xs">
                <label>State mentioned <select aria-label="State mentioned" value={state} onChange={e => onStateChange(e.target.value)} className="ml-2 rounded border border-border bg-background p-2">
                    <option value="">Any state</option>
                    {INDIAN_STATE_OPTIONS.map(value => <option key={value}>{value}</option>)}
                </select></label>
                <label>Country mentioned <input aria-label="Country mentioned" value={country} maxLength={2} placeholder="IN, AE, CN..." title="Two-letter country code" onChange={e => onCountryChange(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} className="ml-2 w-28 rounded border border-border bg-background p-2" /></label>
                <span className="text-muted-foreground">Location mentions describe the message; actor residence is unverified.</span>
            </div>

            {country.length === 1 && <p className="text-xs text-amber-700 dark:text-amber-400">Enter a two-letter country code to apply the country filter.</p>}
            {pending !== undefined && pending > 0 && <p className="text-xs text-muted-foreground">Location and topic filters cover the latest completed analysis. {fmtNum(pending)} retained messages await analysis.</p>}
            {error && <div role="alert" className="text-sm text-destructive">{error} <button type="button" onClick={onSearchSubmit} className="underline">Retry news</button></div>}
            {/* Category toggle */}
            <div className="flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Show:</span>
                {(Object.keys(NEWS_CATEGORY_LABELS) as NewsCategory[]).map(c => (
                    <button key={c} onClick={() => onCategoryChange(c)}
                        className={`px-2.5 py-1 rounded border transition-colors ${
                            category === c
                                ? 'border-primary/50 bg-primary/15 text-foreground font-medium'
                                : 'border-border text-muted-foreground hover:bg-secondary'
                        }`}>
                        {NEWS_CATEGORY_LABELS[c]}
                    </button>
                ))}
            </div>

            {/* Topic chips */}
            <div className="flex items-center gap-1.5 text-xs flex-wrap">
                <span className="text-muted-foreground">Topic:</span>
                <button onClick={() => onTopicChange('')}
                    className={`px-2.5 py-1 rounded border transition-colors ${
                        !topic
                            ? 'border-primary/50 bg-primary/15 text-foreground font-medium'
                            : 'border-border text-muted-foreground hover:bg-secondary'
                    }`}>
                    Any
                </button>
                {NEWS_TOPIC_CHIPS.map(t => (
                    <button key={t.id} onClick={() => onTopicChange(t.id)}
                        className={`px-2.5 py-1 rounded border transition-colors ${
                            topic === t.id
                                ? `border-primary/50 ${TOPIC_COLORS[t.id] || 'bg-primary/15 text-foreground'} font-medium`
                                : 'border-border text-muted-foreground hover:bg-secondary'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Message list */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {items.map((m, i) => (
                    <div key={`${m.channel}-${m.message_id}-${i}`} className="p-3 bg-secondary/20 rounded border border-border">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-foreground font-medium rounded border border-primary/20">#{m.channel}</span>
                            <span className="text-xs font-medium">{m.sender}</span>
                            {m.forward_from && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">
                                    fwd: {m.forward_from}
                                </span>
                            )}
                            {m.has_media && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-800 dark:text-amber-300 rounded border border-amber-500/30">
                                    {m.media_type || 'media'}
                                </span>
                            )}
                            <span className="text-xs text-muted-foreground ml-auto">{fmtDate(m.message_date || m.scraped_at)}</span>
                        </div>
                        <NewsMessageContent content={m.content || ''} />
                        {m.geography && (m.geography.states.length > 0 || m.geography.countries.length > 0) && <div className="mt-2 flex flex-wrap gap-1 text-xs" aria-label="Locations mentioned">
                            {[...m.geography.states, ...m.geography.countries].map(location => <span key={location} title={m.geography?.evidence.filter(e => e.value === location).map(e => e.quote).join(' | ')} className="rounded border border-border px-2 py-0.5">Mentioned: {location}</span>)}
                        </div>}
                        {(m.topic_tags?.length > 0) && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {m.topic_tags.map(tg => (
                                    <span key={tg}
                                        className={`text-[10px] px-1.5 py-0.5 rounded ${TOPIC_COLORS[tg] || 'bg-muted text-foreground/70'}`}>
                                        {tg}
                                    </span>
                                ))}
                            </div>
                        )}
                        {(m.upi_ids?.length > 0 || m.crypto_addresses?.length > 0 || m.tx_hashes?.length > 0) && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {m.upi_ids?.map(u => (
                                    <span key={u} className="text-[10px] px-1.5 py-0.5 bg-green-500/15 text-green-700 dark:text-green-400 rounded font-mono">{u}</span>
                                ))}
                                {m.crypto_addresses?.map(a => (
                                    <button key={a} onClick={() => navigate(`/workbench?address=${a}`)}
                                        className={`text-[10px] px-1.5 py-0.5 rounded font-mono hover:bg-primary/20 ${brandChipBrand}`}>
                                        {a.slice(0, 10)}...
                                    </button>
                                ))}
                                {m.tx_hashes?.map(h => (
                                    <span key={h} className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-foreground rounded font-mono border border-primary/20">
                                        tx:{truncHash(h)}
                                    </span>
                                ))}
                            </div>
                        )}
                        {(m.onion_domains?.length > 0 || m.extracted_domains?.length > 0) && (
                            <div className="flex flex-wrap gap-1 mt-1.5 text-[10px] text-muted-foreground">
                                {m.onion_domains?.map(d => (
                                    <span key={d} className={`px-1.5 py-0.5 rounded ${brandChipMuted}`}>{d}</span>
                                ))}
                                {m.extracted_domains?.slice(0, 6).map(d => (
                                    <span key={d} className="px-1.5 py-0.5 bg-muted rounded">{d}</span>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                {items.length === 0 && !loading && !error && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                        No messages found. {category === 'news' && (
                            <span className="block text-xs mt-2">
                                Topic and location filters become available as archive analysis completes. Review processing progress in Overview.
                            </span>
                        )}
                    </p>
                )}
                {loading && <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
                        className="px-3 py-1 text-xs border border-border rounded disabled:opacity-30">
                        Prev
                    </button>
                    <span className="text-xs text-muted-foreground">Page {page} / {totalPages}</span>
                    <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
                        className="px-3 py-1 text-xs border border-border rounded disabled:opacity-30">
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Senders Tab ───────────────────────────────────────────────────

// ─── OSINT Search Tab ──────────────────────────────────────────────
// Wires the previously-orphaned backend endpoints:
//   GET /scrapers/telegram/search           (full-text + tx hash / address lookup)
//   GET /scrapers/telegram/artifacts        (phones / emails / IFSC / banks / WhatsApp)
//   GET /scrapers/telegram/entity-links     (UPI ↔ wallet pairs)
//   POST /scrapers/telegram/verify-tx-hashes (inline verify button on hash chips)

interface OsintSearchHit {
    channel_name: string;
    message_id: number;
    message_date: string | null;
    sender_name: string | null;
    sender_username: string | null;
    message_content: string | null;
    tx_hashes?: string[] | null;
    crypto_addresses?: string[] | null;
    upi_ids?: string[] | null;
    topic_tags?: string[] | null;
    source_table?: string;
    match_type?: string;
}

interface ArtifactRow {
    channel_name?: string;
    message_id?: number;
    sender_name?: string | null;
    phones?: string[] | null;
    emails?: string[] | null;
    ifsc_codes?: string[] | null;
    bank_mentions?: string[] | null;
    whatsapp_handles?: string[] | null;
}

interface ArtifactSummary {
    total_phones?: number;
    total_emails?: number;
    total_ifsc?: number;
    total_banks?: number;
    total_whatsapp?: number;
}

interface EntityLink {
    upi_id?: string | null;
    wallet_address?: string | null;
    chain?: string | null;
    co_occurrences?: number;
    first_seen?: string | null;
    last_seen?: string | null;
    sender_username?: string | null;
    channel_name?: string | null;
}

function OSINTTab({ navigate }: { navigate: any }) {
    const [query, setQuery] = useState('');
    const [searchHits, setSearchHits] = useState<OsintSearchHit[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchTotal, setSearchTotal] = useState(0);
    const [artifactSummary, setArtifactSummary] = useState<ArtifactSummary | null>(null);
    const [artifacts, setArtifacts] = useState<ArtifactRow[]>([]);
    const [entityLinks, setEntityLinks] = useState<EntityLink[]>([]);
    const [sideLoading, setSideLoading] = useState(true);
    const [verifiedHashes, setVerifiedHashes] = useState<Record<string, boolean | null>>({});

    useEffect(() => {
        // Side panels load once on tab open — artifacts + entity-links are
        // server-paginated, just show the first 30 here.
        setSideLoading(true);
        Promise.all([
            api.get('/scrapers/telegram/artifacts', { params: { limit: 25 } }).catch(() => ({ data: { summary: {}, artifacts: [] } })),
            api.get('/scrapers/telegram/entity-links', { params: { limit: 25 } }).catch(() => ({ data: { entity_links: [] } })),
        ]).then(([artRes, linkRes]) => {
            setArtifactSummary(artRes.data?.summary || null);
            setArtifacts(artRes.data?.artifacts || []);
            setEntityLinks((linkRes.data?.links || []).map((r: EntityLink & { channel?: string }) => ({ ...r, channel_name: r.channel })));
        }).finally(() => setSideLoading(false));
    }, []);

    const runSearch = useCallback(async () => {
        const q = query.trim();
        if (q.length < 2) {
            toast.error('Search needs at least 2 characters (paste a tx hash, address, UPI, or keyword phrase).');
            return;
        }
        setSearchLoading(true);
        try {
            const { data } = await api.get('/scrapers/telegram/search', { params: { q, limit: 50 } });
            setSearchHits(data.messages || []);
            setSearchTotal(data.total_hits ?? (data.messages?.length || 0));
        } catch (e: any) {
            toast.error(`Search failed: ${e?.message || 'unknown error'}`);
        } finally {
            setSearchLoading(false);
        }
    }, [query]);

    const verifyHashes = async (hashes: string[]) => {
        if (!hashes.length) return;
        try {
            const { data } = await api.post('/scrapers/telegram/verify-tx-hashes', { hashes });
            const verdict: Record<string, boolean | null> = {};
            for (const r of data.results || []) {
                verdict[r.tx_hash] = r.verified ?? null;
            }
            setVerifiedHashes(v => ({ ...v, ...verdict }));
        } catch (e: any) {
            toast.error(`Verification failed: ${e?.message || 'unknown'}`);
        }
    };

    return (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* ── Left: Search ── */}
            <div className="lg:col-span-2 space-y-4">
                <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Search className="w-4 h-4 text-primary" />
                        OSINT Search across Telegram data lake
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3">
                        Searches both <code>telegram_raw_archive</code> and <code>telegram_p2p_messages</code> for
                        TX hashes, crypto addresses, UPI IDs, payment IDs, or free-text keywords.
                    </p>
                    <div className="flex gap-2">
                        <input type="text" value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                            placeholder="paste a tx hash / wallet / UPI / keyword..."
                            className="flex-1 bg-muted border border-border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                        <button onClick={runSearch} disabled={searchLoading}
                            className="px-4 py-2 bg-primary hover:bg-brand-gold-hover text-primary-foreground disabled:opacity-50 rounded text-sm font-medium">
                            {searchLoading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                    {searchTotal > 0 && (
                        <div className="text-xs text-muted-foreground mt-2">
                            {searchTotal.toLocaleString()} match{searchTotal === 1 ? '' : 'es'} found
                        </div>
                    )}
                </div>

                {/* Results */}
                <div className="space-y-2">
                    {searchHits.map((h, i) => (
                        <div key={`${h.channel_name}-${h.message_id}-${i}`}
                            className="bg-secondary/20 border border-border rounded p-3">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="text-xs text-muted-foreground">
                                    #{h.channel_name}
                                    {h.sender_username && <span className="ml-2">@{h.sender_username}</span>}
                                    {h.sender_name && !h.sender_username && <span className="ml-2">{h.sender_name}</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    {h.match_type && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-foreground font-medium border border-primary/20">{h.match_type}</span>
                                    )}
                                    <span className="text-[10px] text-muted-foreground">{fmtDate(h.message_date)}</span>
                                </div>
                            </div>
                            <p className="text-sm text-foreground/90 line-clamp-3">{h.message_content || '(no content)'}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                                {(h.crypto_addresses || []).slice(0, 4).map(a => (
                                    <button key={a} onClick={() => navigate?.(`/workbench?address=${encodeURIComponent(a)}`)}
                                        className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/30"
                                        title={`Open ${a} in Workbench`}>
                                        {a.slice(0, 10)}...{a.slice(-4)}
                                    </button>
                                ))}
                                {(h.upi_ids || []).slice(0, 4).map(u => (
                                    <span key={u} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-green-500/10 text-green-600 dark:text-green-400">{u}</span>
                                ))}
                                {(h.tx_hashes || []).slice(0, 3).map(tx => {
                                    const v = verifiedHashes[tx];
                                    return (
                                        <span key={tx}
                                            className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                                v === true ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30' :
                                                v === false ? 'bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/30' :
                                                'bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30'
                                            }`}
                                            title={v === true ? 'Found in local transaction store' : v === false ? 'Not found in tx DB' : 'Click row Verify button to check'}>
                                            {truncHash(tx)}
                                        </span>
                                    );
                                })}
                                {(h.tx_hashes || []).length > 0 && (
                                    <button onClick={() => verifyHashes(h.tx_hashes!)}
                                        className="text-[10px] px-1.5 py-0.5 rounded border border-border hover:bg-muted">
                                        Verify {h.tx_hashes!.length}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    {!searchLoading && searchHits.length === 0 && query.trim().length >= 2 && (
                        <div className="text-center text-sm text-muted-foreground py-8">
                            No matches in the data lake. Try a partial address or a keyword from message content.
                        </div>
                    )}
                </div>
            </div>

            {/* ── Right: Artifacts + Entity Links ── */}
            <div className="space-y-4">
                {/* Artifact summary */}
                <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Hash className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        Extracted Artifacts
                    </h3>
                    {sideLoading ? (
                        <div className="text-xs text-muted-foreground">Loading...</div>
                    ) : artifactSummary ? (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="text-xs"><span className="text-muted-foreground">Phones:</span> <span className="font-semibold text-foreground">{fmtNum(artifactSummary.total_phones || 0)}</span></div>
                            <div className="text-xs"><span className="text-muted-foreground">Emails:</span> <span className="font-semibold text-foreground">{fmtNum(artifactSummary.total_emails || 0)}</span></div>
                            <div className="text-xs"><span className="text-muted-foreground">IFSC:</span> <span className="font-semibold text-foreground">{fmtNum(artifactSummary.total_ifsc || 0)}</span></div>
                            <div className="text-xs"><span className="text-muted-foreground">Banks:</span> <span className="font-semibold text-foreground">{fmtNum(artifactSummary.total_banks || 0)}</span></div>
                            <div className="text-xs col-span-2"><span className="text-muted-foreground">WhatsApp:</span> <span className="font-semibold text-foreground">{fmtNum(artifactSummary.total_whatsapp || 0)}</span></div>
                        </div>
                    ) : null}
                    <div className="space-y-1 max-h-[260px] overflow-y-auto">
                        {artifacts.slice(0, 15).map((a, i) => (
                            <div key={i} className="text-[11px] py-1 border-b border-border/30 last:border-0">
                                <div className="text-muted-foreground">#{a.channel_name} <span className="text-foreground/80">{a.sender_name}</span></div>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                    {(a.phones || []).slice(0, 2).map(p => <span key={p} className="font-mono text-foreground font-medium">{p}</span>)}
                                    {(a.emails || []).slice(0, 2).map(e => <span key={e} className="font-mono text-cyan-700 dark:text-cyan-400">{e}</span>)}
                                    {(a.ifsc_codes || []).slice(0, 2).map(c => <span key={c} className="font-mono text-amber-800 dark:text-amber-300">{c}</span>)}
                                    {(a.bank_mentions || []).slice(0, 2).map(b => <span key={b} className="text-emerald-700 dark:text-emerald-300 font-medium">{b}</span>)}
                                    {(a.whatsapp_handles || []).slice(0, 2).map(w => <span key={w} className="font-mono text-green-700 dark:text-green-300">wa:{w}</span>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Entity Links: UPI ↔ wallet co-occurrences */}
                <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Network className={`w-4 h-4 ${brandIconAccent}`} />
                        UPI ↔ Wallet Links
                    </h3>
                    <div className="space-y-1 max-h-[260px] overflow-y-auto">
                        {sideLoading ? <div className="text-xs text-muted-foreground">Loading...</div> :
                          entityLinks.length === 0 ? <div className="text-xs text-muted-foreground">No UPI-wallet co-occurrences yet.</div> :
                          entityLinks.slice(0, 20).map((l, i) => (
                            <div key={i} className="text-[11px] py-1 border-b border-border/30 last:border-0">
                                {l.upi_id && <span className="font-mono text-green-600 dark:text-green-400">{l.upi_id}</span>}
                                {l.upi_id && l.wallet_address && <span className="text-muted-foreground mx-1">↔</span>}
                                {l.wallet_address && (
                                    <button onClick={() => navigate?.(`/workbench?address=${encodeURIComponent(l.wallet_address!)}`)}
                                        className="font-mono text-emerald-700 dark:text-emerald-400 hover:underline">
                                        {l.wallet_address.slice(0, 8)}...{l.wallet_address.slice(-4)}
                                    </button>
                                )}
                                {l.co_occurrences && l.co_occurrences > 1 && (
                                    <span className="ml-2 text-muted-foreground">×{l.co_occurrences}</span>
                                )}
                                {l.sender_username && <div className="text-muted-foreground/80">@{l.sender_username}</div>}
                            </div>
                          ))
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

function SendersTab({ data, loading, navigate }: { data: IntelSummary | null; loading: boolean; navigate: any }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [actorFilter, setActorFilter] = useState('ALL');
    const [geoFilter, setGeoFilter] = useState('ALL');

    if (loading || !data) return <div className="p-8 text-center text-muted-foreground text-sm">Loading sender intelligence...</div>;

    const crossHits = data.top_cross_hits || [];
    const allSenders = data.top_senders || [];

    const filteredSenders = allSenders.filter((s: Sender) => {
        const matchesSearch = !searchQuery.trim() || 
            (s.sender_name && s.sender_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (s.sender_username && s.sender_username.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (s.notes && s.notes.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesActor = actorFilter === 'ALL' || (s.actor_type && s.actor_type.toUpperCase() === actorFilter);
        const matchesGeo = geoFilter === 'ALL' || (s.geo_scope && s.geo_scope.toUpperCase() === geoFilter);

        return matchesSearch && matchesActor && matchesGeo;
    });

    const getActorBadgeStyle = (actorType?: string) => {
        switch (actorType?.toUpperCase()) {
            case 'SCAMMER':
                return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40';
            case 'HAWALA_BROKER':
                return 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/40';
            case 'CDM_MULE':
                return 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/40';
            case 'MULE_RECRUITER':
                return 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40';
            case 'LAUNDERING_BROKER':
                return 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/40';
            case 'ORDINARY_TRADER':
                return 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30';
            default:
                return 'bg-muted text-muted-foreground border-border';
        }
    };

    const getGeoBadgeStyle = (geoScope?: string) => {
        switch (geoScope?.toUpperCase()) {
            case 'INDIA_DOMESTIC':
                return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30';
            case 'OFFSHORE_DUBAI':
                return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
            case 'INDO_DUBAI_CORRIDOR':
                return 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30';
            case 'OFFSHORE_SE_ASIA':
                return 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30';
            default:
                return 'bg-muted/60 text-muted-foreground border-border/50';
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Tracked Senders" value={data.summary?.sender_features || 0} color="text-foreground" />
                <StatCard
                    label="Cross-Source Hits"
                    value={data.summary?.cross_source_hits || 0}
                    color="text-amber-700 dark:text-amber-400"
                    title="Rows in the Telegram cross-match table: a Telegram sender or identifier matched to a P2P merchant, sanctions entry or entity-DB record. Includes low-confidence fingerprint matches. Not the Harvest Feed's cross-source count, which is identifiers seen in 2+ source types."
                />
                <StatCard label="Phones Found" value={data.summary?.phones_found || 0} color="text-green-700 dark:text-green-400" />
                <StatCard label="Entity Links" value={data.summary?.entity_links || 0} color="text-foreground" />
            </div>

            {/* Top cross-source hits */}
            {crossHits.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        Top Cross-Source Hits
                        <span className="text-xs text-muted-foreground font-normal">
                            Telegram identities matched to P2P merchants / sanctions / entity DB
                        </span>
                    </h3>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {crossHits.map((h, i) => (
                            <div key={i} className="p-3 bg-amber-500/10 rounded border border-amber-500/30">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-900 dark:text-amber-300 font-medium uppercase tracking-wider">
                                            {h.hit_type || 'cross_hit'}
                                        </span>
                                        {h.entity_name && <span className="text-sm font-medium ml-2">{h.entity_name}</span>}
                                        {h.source && <span className="text-xs text-muted-foreground ml-2">[{h.source}]</span>}
                                    </div>
                                    {h.confidence !== null && h.confidence !== undefined && (
                                        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                                            conf {(h.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                                    {h.wallet_address && (
                                        <button onClick={() => navigate?.(`/workbench?address=${encodeURIComponent(h.wallet_address!)}`)}
                                            className="font-mono text-foreground hover:underline">
                                            {h.wallet_address.slice(0, 12)}...{h.wallet_address.slice(-6)}
                                        </button>
                                    )}
                                    {h.upi_id && <span className="font-mono text-green-600 dark:text-green-400">{h.upi_id}</span>}
                                    {h.sender_username && <span>@{h.sender_username}</span>}
                                    {h.channel_name && <span>#{h.channel_name}</span>}
                                    {h.first_seen && <span>since {fmtDate(h.first_seen)}</span>}
                                </div>
                                {h.notes && <p className="text-[11px] text-muted-foreground mt-1 italic">{h.notes}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Senders Search & Filter Bar */}
            <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />
                        Sender Profiles & Intelligence Directory
                        <span className="text-xs font-normal text-muted-foreground">
                            ({filteredSenders.length} displayed)
                        </span>
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Search input */}
                        <div className="relative min-w-[200px]">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search by name, @handle, or notes..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>

                        {/* Actor Type Filter */}
                        <select
                            value={actorFilter}
                            onChange={(e) => setActorFilter(e.target.value)}
                            className="text-xs bg-background border border-border rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                            <option value="ALL">All Actor Types</option>
                            <option value="HAWALA_BROKER">Hawala Brokers</option>
                            <option value="CDM_MULE">CDM Mules</option>
                            <option value="MULE_RECRUITER">Mule Recruiters</option>
                            <option value="LAUNDERING_BROKER">Laundering Brokers</option>
                            <option value="SCAMMER">Scammers</option>
                            <option value="ORDINARY_TRADER">Ordinary Traders</option>
                        </select>

                        {/* Geo Scope Filter */}
                        <select
                            value={geoFilter}
                            onChange={(e) => setGeoFilter(e.target.value)}
                            className="text-xs bg-background border border-border rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                            <option value="ALL">All Regions</option>
                            <option value="INDIA_DOMESTIC">India Domestic</option>
                            <option value="OFFSHORE_DUBAI">Dubai Offshore</option>
                            <option value="INDO_DUBAI_CORRIDOR">Indo-Dubai Corridor</option>
                            <option value="OFFSHORE_SE_ASIA">SE Asia Corridors</option>
                        </select>
                    </div>
                </div>

                {/* Senders Table / Cards */}
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                    {filteredSenders.length === 0 ? (
                        <div className="p-8 text-center text-xs text-muted-foreground border border-dashed border-border rounded-md">
                            No sender profiles match your search criteria.
                        </div>
                    ) : (
                        filteredSenders.map((s: Sender, i: number) => (
                            <div key={s.sender_id || i} className="p-3.5 bg-card/60 hover:bg-card/90 transition-colors rounded-lg border border-border/80 space-y-2 shadow-xs">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-foreground">
                                                {s.sender_name || 'Unnamed Actor'}
                                            </span>
                                            {s.sender_username && (
                                                <span className="text-xs text-muted-foreground font-mono">
                                                    @{s.sender_username}
                                                </span>
                                            )}

                                            {/* Actor Type Badge */}
                                            {s.actor_type && s.actor_type !== 'UNKNOWN' && (
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${getActorBadgeStyle(s.actor_type)}`}>
                                                    {s.actor_type.replace('_', ' ')}
                                                </span>
                                            )}

                                            {/* Geo Scope Badge */}
                                            {s.geo_scope && s.geo_scope !== 'UNKNOWN' && (
                                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${getGeoBadgeStyle(s.geo_scope)}`}>
                                                    {s.geo_scope.replace('_', ' ')}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-xs text-muted-foreground font-mono">
                                            {s.message_count} msgs | {s.channel_count} ch
                                        </span>
                                        {s.risk_score > 0.2 && (
                                            <span className={`text-xs px-2 py-0.5 rounded font-mono font-medium ${
                                                s.risk_score >= 0.7 
                                                    ? 'bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30' 
                                                    : 'bg-amber-500/15 text-amber-800 dark:text-amber-400 border border-amber-500/30'
                                            }`}>
                                                Risk: {(s.risk_score * 100).toFixed(0)}%
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Forensic Notes Box */}
                                {s.notes && (
                                    <div className="text-xs p-2.5 rounded-md bg-secondary/30 border border-border/50 text-foreground/90 font-sans leading-relaxed">
                                        <span className="font-semibold text-primary mr-1.5 uppercase text-[10px] tracking-wider">
                                            Forensic Notes:
                                        </span>
                                        {s.notes}
                                    </div>
                                )}

                                {/* Bio / About text if present */}
                                {s.bio && (
                                    <div className="text-[11px] text-muted-foreground italic">
                                        <span className="font-medium text-foreground not-italic mr-1">About Me:</span>
                                        "{s.bio}"
                                    </div>
                                )}

                                {/* Metrics & Extracted Artifacts */}
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-0.5">
                                    {s.unique_upi_count > 0 && (
                                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                            {s.unique_upi_count} UPI ID(s)
                                        </span>
                                    )}
                                    {s.unique_wallet_count > 0 && (
                                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                                            {s.unique_wallet_count} Wallet(s)
                                        </span>
                                    )}
                                    <span>{s.avg_messages_per_day.toFixed(1)} msg/day</span>
                                    {s.first_seen && <span>Active since {fmtDate(s.first_seen)}</span>}
                                    {s.last_seen && <span>Last seen {fmtDate(s.last_seen)}</span>}
                                </div>

                                {/* Discovered Links to Other Groups/Counterparties */}
                                {s.discovered_links && s.discovered_links.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                                            <Link2 className="w-3 h-3" /> Discovered Groups:
                                        </span>
                                        {s.discovered_links.map((link) => (
                                            <span key={link} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-mono">
                                                @{link}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Risk Flags */}
                                {s.risk_flags && s.risk_flags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 pt-0.5">
                                        {(typeof s.risk_flags === 'string' ? JSON.parse(s.risk_flags) : s.risk_flags).map((f: string) => (
                                            <span key={f} className="text-[10px] px-1.5 py-0.2 bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20 rounded font-medium">
                                                {f}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Deep Analysis Tab ─────────────────────────────────────────────

function DeepAnalysisTab({ data, loading, processing, runProcessing, expandedSection, setExpandedSection, navigate }: {
    data: DeepIntelData | null; loading: boolean; processing: boolean;
    runProcessing: () => void; expandedSection: string | null;
    setExpandedSection: (s: string | null) => void; navigate: any;
}) {
    const toggle = (s: string) => setExpandedSection(expandedSection === s ? null : s);
    const arch = data?.archive;
    const unprocessed = arch ? arch.total_messages - arch.processed : 0;

    if (loading && !data) return <div className="p-8 text-center text-muted-foreground text-sm">Loading deep intelligence...</div>;
    if (!data) return <div className="p-8 text-center text-muted-foreground text-sm">No data available. Run processing first.</div>;

    return (
        <div className="divide-y divide-border">
            {/* Process button bar */}
            {unprocessed > 0 && (
                <div className="p-4 flex items-center justify-between bg-primary/5">
                    <span className="text-sm text-muted-foreground">{fmtNum(unprocessed)} unprocessed messages in raw archive</span>
                    <button onClick={runProcessing} disabled={processing}
                        className={`${brandPrimaryBtnMd} disabled:opacity-50 flex items-center gap-1`}>
                        <RefreshCw className={`w-3 h-3 ${processing ? 'animate-spin' : ''}`} />
                        {processing ? 'Processing...' : 'Process Now'}
                    </button>
                </div>
            )}

            {/* Overview */}
            <Section title="Overview" icon={<Shield className="w-4 h-4" />} id="overview"
                expanded={expandedSection === 'overview'} onToggle={() => toggle('overview')}
                badge={`${fmtNum(arch?.processed || 0)} processed`}>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    <StatCard label="Total Messages" value={arch?.total_messages || 0} color="text-foreground" />
                    <StatCard label="Spam / Phishing" value={arch?.spam_messages || 0} color="text-red-700 dark:text-red-400" />
                    <StatCard label="With Addresses" value={arch?.with_crypto_addrs || 0} color="text-green-700 dark:text-green-400" />
                    <StatCard label="Onion Refs" value={arch?.onion_messages || 0} color="text-foreground" />
                    <StatCard label="Marketplace" value={arch?.marketplace_listings || 0} color="text-indigo-700 dark:text-indigo-400" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {data.topic_distribution.map(t => (
                        <span key={t.tag} className={`px-2 py-0.5 rounded text-xs font-medium ${TOPIC_COLORS[t.tag] || 'bg-muted text-foreground'}`}>
                            {t.tag.replace(/_/g, ' ')}: {fmtNum(t.cnt)}
                        </span>
                    ))}
                </div>
            </Section>

            {/* Scam Domains */}
            <Section title="Scam & Phishing Domains" icon={<AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />}
                id="scam" expanded={expandedSection === 'scam'} onToggle={() => toggle('scam')}
                badge={`${data.scam_domains.length} domains`}>
                <div className="space-y-2">
                    {data.scam_domains.map(d => (
                        <div key={d.domain} className="flex items-center justify-between p-2.5 bg-red-500/10 rounded border border-red-500/30">
                            <div>
                                <span className="text-sm font-mono text-red-700 dark:text-red-400 font-medium">{d.domain}</span>
                                <div className="text-xs text-muted-foreground mt-0.5">{d.mention_count} mentions in {d.channels_seen?.join(', ')}</div>
                            </div>
                            <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-800 dark:text-red-300 font-medium rounded">{d.classification}</span>
                        </div>
                    ))}
                    {data.scam_domains.length === 0 && <p className="text-xs text-muted-foreground">No scam domains detected.</p>}
                </div>
            </Section>

            {/* Cross-Channel Users */}
            <Section title="Cross-Channel Identities" icon={<Users className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />}
                id="crosschannel" expanded={expandedSection === 'crosschannel'} onToggle={() => toggle('crosschannel')}
                badge={`${data.cross_channel_users.total} users in 2+ channels`}>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                    {data.cross_channel_users.top.map(u => (
                        <div key={u.sender_id} className="p-2.5 bg-secondary/30 rounded border border-border">
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-sm font-medium">{u.sender_name || 'Unknown'}</span>
                                    {u.sender_username && <span className="text-xs text-muted-foreground ml-2">@{u.sender_username}</span>}
                                </div>
                                <span className="text-xs text-muted-foreground">{fmtNum(u.total_messages)} msgs</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {u.channels.map((ch: string) => <span key={ch} className="text-xs px-1.5 py-0.5 bg-primary/10 text-foreground font-medium rounded border border-primary/20">{ch}</span>)}
                            </div>
                            <div className="flex gap-2 mt-1 text-xs">
                                {u.has_crypto_addresses && <span className="text-green-700 dark:text-green-400 font-medium">has addresses</span>}
                                {u.has_trade_intent && <span className="text-amber-700 dark:text-amber-400 font-medium">trade intent</span>}
                                {u.services_mentioned?.length > 0 && <span className="text-muted-foreground">svcs: {u.services_mentioned.slice(0, 3).join(', ')}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Network Graph */}
            <Section title="Telegram Group Network" icon={<Network className="w-4 h-4 text-foreground" />}
                id="network" expanded={expandedSection === 'network'} onToggle={() => toggle('network')}
                badge={`${data.network_graph.unique_groups} groups referenced`}>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {data.network_graph.top_refs.map(r => (
                        <div key={r.target_group} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-mono text-foreground font-medium">t.me/{r.target_group}</span>
                                <span className="text-xs text-muted-foreground">from: {r.from_channels?.join(', ')}</span>
                            </div>
                            <span className="text-xs font-medium">{r.total_refs}x</span>
                        </div>
                    ))}
                </div>
            </Section>

            {/* URL TX Hashes */}
            <Section title="TX Hashes from Explorer URLs" icon={<Hash className="w-4 h-4 text-green-600 dark:text-green-400" />}
                id="txhashes" expanded={expandedSection === 'txhashes'} onToggle={() => toggle('txhashes')}
                badge={`${data.url_tx_hashes.total} extracted`}>
                <div className="space-y-1.5">
                    {data.url_tx_hashes.samples.map((tx, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
                            <div>
                                <button onClick={() => navigate(`/workbench?tx=${tx.tx_hash}`)} className="text-xs font-mono text-green-700 dark:text-green-400 hover:underline font-medium">
                                    {truncHash(tx.tx_hash)}
                                </button>
                                <div className="text-xs text-muted-foreground">
                                    {tx.chain_guess && <span className="mr-2 text-blue-700 dark:text-blue-400 font-medium">{tx.chain_guess}</span>}
                                    {tx.channel_name} | {tx.message_date?.slice(0, 10)}
                                </div>
                            </div>
                            {tx.explorer_domain && <span className="text-xs text-muted-foreground">{tx.explorer_domain}</span>}
                        </div>
                    ))}
                    {data.url_tx_hashes.total === 0 && <p className="text-xs text-muted-foreground">No TX hashes found in URLs.</p>}
                </div>
            </Section>

            {/* Darknet / Onion */}
            {data.onion_domains.length > 0 && (
                <Section title="Darknet / .onion Domains" icon={<Globe className={`w-4 h-4 ${brandIconAccent}`} />}
                    id="onion" expanded={expandedSection === 'onion'} onToggle={() => toggle('onion')}
                    badge={`${data.onion_domains.length} domains`}>
                    <div className="space-y-1">
                        {data.onion_domains.map(d => (
                            <div key={d.domain} className="flex items-center justify-between p-1.5">
                                <span className="text-xs font-mono text-foreground break-all">{d.domain}</span>
                                <span className="text-xs text-muted-foreground">{d.cnt}x</span>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Marketplace */}
            {data.marketplace_listings.length > 0 && (
                <Section title="Marketplace Listings" icon={<ShoppingBag className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                    id="marketplace" expanded={expandedSection === 'marketplace'} onToggle={() => toggle('marketplace')}
                    badge={`${data.marketplace_listings.length} listings`}>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                        {data.marketplace_listings.map((m, i) => (
                            <div key={i} className="p-2.5 bg-secondary/30 rounded border border-border">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${m.marketplace_tag === 'selling' ? 'bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30' : m.marketplace_tag === 'buying' ? 'bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30' : 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30'}`}>
                                        #{m.marketplace_tag}
                                    </span>
                                    <span className="text-xs text-muted-foreground">{m.channel_name}</span>
                                    {m.sender_username && <span className="text-xs text-cyan-700 dark:text-cyan-400 font-medium">@{m.sender_username}</span>}
                                    <span className="text-xs text-muted-foreground ml-auto">{m.message_date?.slice(0, 10)}</span>
                                </div>
                                <p className="text-xs text-foreground/80 leading-relaxed">{m.preview}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Top Domains */}
            <Section title="Top Shared Domains" icon={<Globe className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                id="domains" expanded={expandedSection === 'domains'} onToggle={() => toggle('domains')}
                badge={`${data.top_domains.length}`}>
                <div className="flex flex-wrap gap-1.5">
                    {data.top_domains.map(d => (
                        <span key={d.domain} className="text-xs px-2 py-0.5 bg-emerald-500/15 text-emerald-800 dark:text-emerald-400 rounded border border-emerald-500/30 font-mono">
                            {d.domain} ({d.cnt})
                        </span>
                    ))}
                </div>
            </Section>
        </div>
    );
}

// ─── Exchanges Tab (VDA SP Directory) ──────────────────────────────

const CATEGORY_LABELS: Record<ProviderCategory, string> = {
    fiu_onshore: 'FIU Onshore',
    fiu_offshore: 'FIU Offshore',
    offshore: 'Offshore',
    local_p2p: 'Local P2P',
};

const CATEGORY_COLORS: Record<ProviderCategory, string> = {
    fiu_onshore: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400',
    fiu_offshore: 'border-blue-500/40 text-blue-700 dark:text-blue-400',
    offshore: 'border-amber-500/40 text-amber-700 dark:text-amber-400',
    local_p2p: 'border-primary/40 text-foreground',
};

function ExchangesTab({
    providers, selectedCategories, setSelectedCategories,
    search, setSearch, expandedId, setExpandedId,
    canBackfill, onBackfillChannel, onJumpToMessages,
}: {
    providers: Provider[];
    selectedCategories: Set<ProviderCategory>;
    setSelectedCategories: (s: Set<ProviderCategory>) => void;
    search: string; setSearch: (v: string) => void;
    expandedId: number | null; setExpandedId: (id: number | null) => void;
    canBackfill: boolean;
    onBackfillChannel: (handle: string) => void;
    onJumpToMessages: (handle: string) => void;
}) {
    const toggleCategory = (cat: ProviderCategory) => {
        const next = new Set(selectedCategories);
        if (next.has(cat)) next.delete(cat); else next.add(cat);
        setSelectedCategories(next);
    };

    const filtered = providers.filter(p => {
        if (!selectedCategories.has(p.category)) return false;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            return (
                p.display_name.toLowerCase().includes(q) ||
                p.slug.toLowerCase().includes(q) ||
                (p.telegram_public_handle || '').toLowerCase().includes(q)
            );
        }
        return true;
    });

    return (
        <div className="p-6 space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1.5">
                    {(Object.keys(CATEGORY_LABELS) as ProviderCategory[]).map(cat => {
                        const checked = selectedCategories.has(cat);
                        const count = providers.filter(p => p.category === cat).length;
                        return (
                            <button
                                key={cat}
                                onClick={() => toggleCategory(cat)}
                                className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                                    checked
                                        ? `${CATEGORY_COLORS[cat]} bg-secondary/50`
                                        : 'border-border text-muted-foreground hover:bg-secondary/30'
                                }`}
                            >
                                {checked && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                                {CATEGORY_LABELS[cat]} ({count})
                            </button>
                        );
                    })}
                </div>
                <div className="flex-1 min-w-[180px] relative">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name, slug, or handle..."
                        className="w-full pl-10 pr-3 py-2 text-sm bg-secondary/50 border border-border rounded"
                    />
                </div>
                <span className="text-xs text-muted-foreground">{filtered.length} of {providers.length}</span>
            </div>

            {/* Directory table */}
            <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-xs">
                    <thead className="bg-secondary/30 text-muted-foreground border-b border-border">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Provider</th>
                            <th className="text-left px-3 py-2 font-medium">Category</th>
                            <th className="text-left px-3 py-2 font-medium">FIU status</th>
                            <th className="text-left px-3 py-2 font-medium">Telegram</th>
                            <th className="text-right px-3 py-2 font-medium">Msgs (30d)</th>
                            <th className="text-right px-3 py-2 font-medium">P2P ads (7d)</th>
                            <th className="text-right px-3 py-2 font-medium">Spot rows (7d)</th>
                            <th className="text-right px-3 py-2 font-medium">Last announcement</th>
                            <th className="text-right px-3 py-2 font-medium">Links</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(p => {
                            const expanded = expandedId === p.id;
                            const tg = p.telegram_signals;
                            const p2p = p.p2p_signals;
                            return (
                                <Fragment key={p.id}>
                                    <tr
                                        className="border-b border-border/30 hover:bg-secondary/20 cursor-pointer"
                                        onClick={() => setExpandedId(expanded ? null : p.id)}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="font-medium">{p.display_name}</div>
                                            <div className="text-[10px] text-muted-foreground">{p.slug}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <CategoryBadge category={p.category} label={CATEGORY_LABELS[p.category]} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <SeverityBadge
                                                level={FIU_STATUS_TO_SEVERITY[p.fiu_status]}
                                                label={FIU_STATUS_LABELS[p.fiu_status] ?? p.fiu_status.replace('_', ' ').toUpperCase()}
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {p.telegram_public_handle ? (
                                                <span className="font-mono">@{p.telegram_public_handle}</span>
                                            ) : p.telegram_invite_url ? (
                                                <span className="italic text-amber-700 dark:text-amber-400 font-medium">private (invite-only)</span>
                                            ) : (
                                                <span className="italic">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {tg ? fmtNum(tg.msg_30d) : <span className="text-muted-foreground/60">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {p2p ? fmtNum(p2p.p2p_ads_7d) : <span className="text-muted-foreground/60">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {p2p ? fmtNum(p2p.spot_rows_7d) : <span className="text-muted-foreground/60">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right text-[10px] text-muted-foreground">
                                            {tg?.last_announcement_at
                                                ? fmtDate(tg.last_announcement_at)
                                                : '—'}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                                                {p.website && (
                                                    <a href={p.website} target="_blank" rel="noopener noreferrer"
                                                        className="text-muted-foreground hover:text-foreground" title={p.website}>
                                                        <Globe className="w-3 h-3" />
                                                    </a>
                                                )}
                                                {p.twitter_handle && (
                                                    <a href={`https://x.com/${p.twitter_handle}`} target="_blank" rel="noopener noreferrer"
                                                        className="text-muted-foreground hover:text-foreground" title={`@${p.twitter_handle}`}>
                                                        <Twitter className="w-3 h-3" />
                                                    </a>
                                                )}
                                                {p.telegram_public_handle && (
                                                    <a href={`https://t.me/${p.telegram_public_handle}`} target="_blank" rel="noopener noreferrer"
                                                        className="text-muted-foreground hover:text-foreground" title="Open in Telegram">
                                                        <MessageSquare className="w-3 h-3" />
                                                    </a>
                                                )}
                                                {p.telegram_invite_url && (
                                                    <a href={p.telegram_invite_url} target="_blank" rel="noopener noreferrer"
                                                        className="text-amber-700 dark:text-amber-400 hover:text-amber-600 dark:hover:text-amber-300" title="Private invite (session must join)">
                                                        <Lock className="w-3 h-3" />
                                                    </a>
                                                )}
                                                <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
                                            </div>
                                        </td>
                                    </tr>
                                    {expanded && (
                                        <tr className="bg-secondary/10 border-b border-border/30">
                                            <td colSpan={9} className="px-4 py-3">
                                                <div className="grid gap-2 text-xs">
                                                    {p.notes && (
                                                        <div>
                                                            <span className="text-muted-foreground">Notes: </span>
                                                            <span className="text-foreground/80">{p.notes}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                                                        {p.fiu_registration_date && (
                                                            <span>FIU registered: <strong>{p.fiu_registration_date}</strong></span>
                                                        )}
                                                        {tg && (
                                                            <span>Telegram total messages: <strong>{fmtNum(tg.msg_total)}</strong></span>
                                                        )}
                                                        {p2p && (
                                                            <span>Last scrape: <strong>{p2p.last_p2p_scrape ? fmtDate(p2p.last_p2p_scrape) : '—'}</strong></span>
                                                        )}
                                                    </div>
                                                    {p.telegram_public_handle && (
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                onClick={() => onJumpToMessages(p.telegram_public_handle!)}
                                                                className="text-[10px] text-foreground hover:underline flex items-center gap-1 font-medium"
                                                            >
                                                                View scraped messages from @{p.telegram_public_handle}
                                                                <ExternalLink className="w-3 h-3" />
                                                            </button>
                                                            {canBackfill && (
                                                                <button
                                                                    onClick={() => onBackfillChannel(p.telegram_public_handle!)}
                                                                    className="text-[10px] text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1 font-medium"
                                                                    title="Admin: pull full channel history (up to 50,000 messages). This may take several minutes."
                                                                >
                                                                    <RefreshCw className="w-3 h-3" />
                                                                    Deep backfill
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground text-xs">
                                    No providers match the current filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <p className="text-[10px] text-muted-foreground italic">
                Telegram message + P2P ad counts join from <code>telegram_p2p_messages</code> and <code>p2p_advertisements</code>.
                Providers without a verified handle ("—") will show 0 messages — fill in via{' '}
                <code>scripts/seed_vda_sp_providers.py</code> or have an admin add the channel via the Channels tab.
            </p>
        </div>
    );
}

// ─── Shared Sub-components ─────────────────────────────────────────

function Section({ title, icon, id, expanded, onToggle, badge, children }: {
    title: string; icon: React.ReactNode; id: string;
    expanded: boolean; onToggle: () => void; badge?: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-2">
                    {icon}
                    <span className="text-sm font-medium">{title}</span>
                    {badge && <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">{badge}</span>}
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {expanded && <div className="px-5 pb-5">{children}</div>}
        </div>
    );
}

function StatCard({ label, value, color, title }: { label: string; value: number; color: string; title?: string }) {
    return (
        <div className={`bg-secondary/30 rounded p-3 text-center${title ? ' cursor-help' : ''}`} title={title}>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className={`text-lg font-bold ${color}`}>{fmtNum(value)}</div>
        </div>
    );
}
